import { useEffect, useState, useRef } from "react";
import "App.css";
import { HashRouter, Navigate, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { toast, Toaster } from "sonner";
import { Shield, Laptop, Globe, Star, Phone, Mail, MapPin, Menu, X, Search, AlertCircle, CheckCircle, Clock, Scroll, ArrowRight, ChevronDown, Users, Briefcase, ShieldCheck, Zap, Cpu, Rocket, Award, MessageCircle, Video, HardDrive, Eye, Home as HomeIcon, Building2, Map, ShieldAlert, Headphones, Layout, Thermometer, Monitor, Database } from "lucide-react";
import { motion, AnimatePresence, useScroll, useSpring, useTransform, animate, useMotionValue, useInView } from "framer-motion";
import Logo from "./logo/logo.png"
import MorphingText from "./components/MorphingText";
import TechParticles from "./components/TechParticles";
import AOS from "aos";
import "aos/dist/aos.css";
import ScrollToTop from "./components/ScroolToTop.jsx";
import { hardwareimg, cctvimage, networkingimage, mobileimage, salesimage, systemimage, pcbg } from "./images/index.js";
axios.defaults.withCredentials = true;



const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = "https://auth.emergentagent.com";

const Navigation = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      (isHomePage && !isScrolled && !mobileMenuOpen)
        ? "bg-transparent border-transparent"
        : "bg-white/80 backdrop-blur-xl border-b border-slate-200"
    }`}>
      <div className="w-full px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 w-full">
          {/* Left: Logo */}
          <div className="flex-1 flex justify-start">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-16 h-16 bg-gradient-to-br rounded-lg flex items-center justify-center">
                <img src={Logo} alt="logo" height="400px" width="400px" />
              </div>
              <span className="text-2xl font-bold text-slate-900">V4 TECH</span>
            </Link>
          </div>

          {/* Center: Nav links */}
          <div className="hidden md:flex justify-center items-center space-x-8">
            <Link to="/" className="text-slate-600 hover:text-[#D4AF37]">Home</Link>
            <Link to="/about" className="text-slate-600 hover:text-[#D4AF37]">About</Link>
            <div className="relative group flex items-center h-full">
              <span className="text-slate-600 group-hover:text-[#D4AF37] transition-all duration-300 cursor-pointer flex items-center gap-1">
                Services
                <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
              </span>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="w-64 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                  <div className="p-3 bg-gradient-to-br from-slate-50 to-white space-y-1">
                    {[
                      { name: "Security and Surveillance", icon: Shield, path: "/services/security-surveillance-v2" },
                      { name: "Computer & Laptop Service", icon: Laptop, path: "/services/computer-laptop-service" },
                      { name: "Networking Solution", icon: Globe, path: "/services/networking-solution" },
                      { name: "System Customization", icon: Laptop, path: "/services/system-customization" },
                      { name: "Sales & Retail", icon: Search, path: "/services/sales-retail" },
                      { name: "Mobile Solutions", icon: Phone, path: "/services/mobile-solution" }
                    ].map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.path}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-white hover:text-[#D4AF37] hover:shadow-md transition-all duration-200 rounded-xl group/item"
                      >
                        <div className="p-2 bg-slate-100 group-hover/item:bg-[#D4AF3710] rounded-lg transition-colors">
                          <item.icon size={16} />
                        </div>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <Link to="/reviews" className="text-slate-600 hover:text-[#D4AF37]">Reviews</Link>
            <Link to="/support" className="text-slate-600 hover:text-[#D4AF37]">Support</Link>
            <Link to="/contact" className="text-slate-600 hover:text-[#D4AF37]">Contact</Link>
          </div>

          {/* Right: Admin login / Mobile menu */}
          <div className="flex-1 flex justify-end items-center space-x-4">
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  <Link to="/admin" className="text-slate-600 hover:text-[#D4AF37]">Admin</Link>
                  <Button onClick={onLogout} variant="outline" size="sm" data-testid="logout-btn">Logout</Button>
                </>
              ) : (
                <Button onClick={() => navigate('/admin-login')} size="sm" data-testid="admin-login-btn">Admin Login</Button>
              )}
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="md:hidden mt-4 p-8 rounded-[2.5rem] bg-white/90 backdrop-blur-2xl border border-slate-200/60 shadow-[0_20px_50px_rgba(0,0,0,0.15)] space-y-3 overflow-hidden relative z-50"
              data-testid="mobile-menu"
            >
              {/* Subtle background highlight for the menu */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/5 rounded-bl-full pointer-events-none"></div>

            <Link to="/" className="block py-3 text-slate-600 hover:text-[#D4AF37] font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/about" className="block py-3 text-slate-600 hover:text-[#D4AF37] font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between py-2 text-slate-600 hover:text-[#D4AF37]"
              >
                <span>Services</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {mobileServicesOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden space-y-1 pl-4 border-l-2 border-[#D4AF37]/30"
                  >
                    {[
                      { name: "Security and Surveillance", path: "/services/security-surveillance-v2" },
                      { name: "Computer & Laptop Service", path: "/services/computer-laptop-service" },
                      { name: "Networking Solution", path: "/services/networking-solution" },
                      { name: "System Customization", path: "/services/system-customization" },
                      { name: "Sales & Retail", path: "/services/sales-retail" },
                      { name: "Mobile Solutions", path: "/services/mobile-solution" }
                    ].map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.path}
                        className="block py-2.5 text-slate-600 hover:text-[#D4AF37] text-sm transition-colors font-medium"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileServicesOpen(false);
                        }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/reviews" className="block py-3 text-slate-600 hover:text-[#D4AF37] font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Reviews</Link>
            <Link to="/support" className="block py-3 text-slate-600 hover:text-[#D4AF37] font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Support</Link>
            <Link to="/contact" className="block py-3 text-slate-600 hover:text-[#D4AF37] font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            {/* {user ? (
              <>
                <Link to="/admin" className="block py-2 text-slate-600 hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
                <Button onClick={onLogout} variant="outline" size="sm" className="w-full mt-2">Logout</Button>
              </>
            ) : (
              <Button onClick={() => navigate('/admin-login')} size="sm" className="w-full mt-2">Admin Login</Button>
            )} */}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

const services = [
  {
    title: "Security and Surveillance",
    desc: "Advanced CCTV systems, remote monitoring, and comprehensive property protection solutions.",
    icon: Shield,
    side: "left",
    count: "01",
    tags: ["HD Quality", "Remote Access"]
  },
  {
    title: "Computer & Laptop Service",
    desc: "Expert hardware repairs, software optimization, and complete maintenance for all computing devices.",
    icon: Laptop,
    side: "right",
    count: "02",
    tags: ["Quick Fix", "Genuine Parts"]
  },
  {
    title: "Networking Solution",
    desc: "Seamless office/home networking, Wi-Fi optimization, and professional infrastructure setup.",
    icon: Globe,
    side: "left",
    count: "03",
    tags: ["High Speed", "Enterprise"]
  },
  {
    title: "System Customization",
    desc: "Custom PC builds, workstation optimization, and hardware upgrades tailored to your workflow.",
    icon: Search,
    side: "right",
    count: "04",
    tags: ["OC Support", "Precision"]
  },
  {
    title: "Sales & Retail",
    desc: "Premium Laptops, CCTV kits, and genuine tech accessories at competitive market prices.",
    icon: Clock,
    side: "left",
    count: "05",
    tags: ["Best Price", "Warranty"]
  },
  {
    title: "Mobile Solutions",
    desc: "Expert chip-level repairs, display replacements, and advanced data recovery for all major smartphone brands.",
    icon: Phone,
    side: "right",
    count: "06",
    tags: ["Chip-Level", "Data Recovery"]
  }
];

const Counter = ({ value }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const numericValue = parseInt(value?.toString().replace(/\D/g, '') || 0);
  const suffix = value?.toString().replace(/[0-9]/g, '') || "";
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      count.set(0);
      const controls = animate(count, numericValue, {
        duration: 2.5,
        ease: "easeOut",
        delay: 0.2
      });
      return controls.stop;
    }
  }, [numericValue, count, isInView]);

  useEffect(() => {
    return rounded.on("change", (latest) => setDisplayValue(latest));
  }, [rounded]);

  return <span ref={ref}>{displayValue}{suffix}</span>;
};

const Home = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [publicStats, setPublicStats] = useState({ customers: "500+", projects: "1200+", team: "15+" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          axios.get(`${API}/reviews`),
          axios.get(`${API}/public-stats`)
        ]);
        setReviews(reviewsRes.data.slice(0, 3));
        setPublicStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative h-screen flex items-center justify-center overflow-hidden" >
        {/* Dynamic Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.08] pointer-events-none z-0"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2940&auto=format&fit=crop')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/70 to-white pointer-events-none z-0"></div>

        <TechParticles />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-5xl font-bold text-slate-900 mb-6">
            <MorphingText />
          </h1>
          <p className="text-xl sm:text-xl text-slate-800 mb-12 max-w-3xl mx-auto">
            From 4K AI-Powered CCTV Systems to Precision Chip-Level Hardware Services V4 TECH delivers reliable, future-ready technology solutions with 100% genuine parts and expert support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-lg px-8 py-6 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF3720]" variant="outline" onClick={() => navigate('/contact')} data-testid="get-in-touch-btn">
              Get In Touch
            </Button>
          </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-14 flex flex-wrap justify-center items-center gap-4 sm:gap-x-6 gap-y-3 text-sm font-semibold text-slate-700 bg-white/50 backdrop-blur-md py-3 px-6 sm:px-10 rounded-full border border-white shadow-sm shadow-[#D4AF37]/5 mx-auto w-fit"
          >
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-[#D4AF37]" /> 100% Genuine Spares</span>
            <div className="hidden lg:block w-1.5 h-1.5 rounded-full bg-slate-300"></div>
            <span className="flex items-center gap-2"><Award size={16} className="text-[#D4AF37]" /> Certified Engineers</span>
            <div className="hidden lg:block w-1.5 h-1.5 rounded-full bg-slate-300"></div>
            <span className="flex items-center gap-2"><Zap size={16} className="text-[#D4AF37]" /> On-Site Support</span>
            <div className="hidden lg:block w-1.5 h-1.5 rounded-full bg-slate-300"></div>
            <span className="flex items-center gap-2"><Users size={16} className="text-[#D4AF37]" /> 200+ Happy Clients</span>
          </motion.div>
        </div>
      </section>

      {/* The Big Three Grid */}
      <section className="relative z-20 -mt-16 sm:-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Security", desc: "AI-Powered Surveillance." },
            { icon: Cpu, title: "Hardware", desc: "Chip-Level Repairs & Custom Builds." },
            { icon: Globe, title: "Networking", desc: "Enterprise Connectivity." }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + (idx * 0.1) }}
              className="bg-white/90 backdrop-blur-xl p-8 rounded-3xl border border-slate-200/50 shadow-2xl shadow-slate-200/30 hover:shadow-[#D4AF37]/20 transition-all group flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5"
            >
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:bg-[#D4AF37] shadow-inner transition-colors border border-slate-100 shrink-0">
                <item.icon className="text-[#D4AF37] group-hover:text-black transition-colors" size={32} />
              </div>
              <div className="pt-1">
                <h3 className="font-extrabold text-slate-900 text-xl mb-1 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                <p className="text-slate-600 font-semibold">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="pt-20 pb-32 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        {/* Dynamic Background Image for Services */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2940&auto=format&fit=crop')] bg-cover bg-center bg-no-repeat opacity-[0.05] pointer-events-none z-0"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/80 to-white/95 pointer-events-none z-0"></div>

        {/* Fill empty section space with tech background grid */}
        <div className="tech-grid-bg opacity-20 will-change-transform z-0"></div>
        <div className="tech-grid-fade z-0"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[#D4AF37] font-semibold tracking-widest uppercase text-sm mb-3 block"
            >
              What We Offer
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6"
            >
              Our Premium Services
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="w-24 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-6"
            ></motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-lg text-slate-600 max-w-2xl mx-auto"
            >
              Professional tech solutions tailored to your specific needs, delivered with excellence and precision.
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto relative">
            {/* The Connecting Line (Vertical) with Glowing Pulse Progress */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-slate-100 hidden md:block overflow-hidden rounded-full">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: "100%" }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="absolute top-0 left-0 right-0 bg-gradient-to-b from-[#D4AF37] via-[#F4D03F] to-[#D4AF37] shadow-[0_0_15px_rgba(212,175,55,1)] will-change-[height]"
              />
            </div>

            <div className="space-y-32 md:space-y-0 relative">
              {services.map((service, index) => (
                <div key={index} className={`flex flex-col md:flex-row items-center justify-between mb-32 md:mb-56 last:mb-0 ${service.side === 'right' ? 'md:flex-row-reverse' : ''}`}>
                  {/* Content Card with Advanced Glassmorphism */}
                  <motion.div
                    initial={{ opacity: 0, x: service.side === 'left' ? -120 : 120, y: 50, rotateZ: service.side === 'left' ? -5 : 5 }}
                    whileInView={{ opacity: 1, x: 0, y: 0, rotateZ: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1, type: "spring", bounce: 0.3 }}
                    className={`w-full md:w-[46%] group relative bg-white/95 p-10 sm:p-12 rounded-[4rem] border border-slate-100 shadow-[0_15px_60px_-15px_rgba(0,0,0,0.08)] hover:shadow-[#D4AF3715] transition-all duration-300 overflow-visible text-center md:text-left ${service.side === 'right' ? 'md:text-right' : ''}`}
                  >
                    {/* Floating Decorative Tags */}
                    <div className={`absolute -top-6 ${service.side === 'left' ? 'left-10' : 'right-10'} flex gap-3`}>
                      {service.tags.map((tag, tIdx) => (
                        <motion.span
                          key={tIdx}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.5 + (tIdx * 0.1) }}
                          className="bg-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-tighter text-[#D4AF37] shadow-xl border border-slate-50"
                        >
                          {tag}
                        </motion.span>
                      ))}
                    </div>

                    <div className={`absolute top-0 ${service.side === 'left' ? 'right-0' : 'left-0'} w-64 h-64 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent rounded-full -m-24 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 blur-3xl`}></div>

                    <span className={`block text-8xl font-black text-slate-200/40 mb-2 transition-all duration-700 group-hover:text-[#D4AF3720] group-hover:-translate-y-4 select-none ${service.side === 'right' ? 'md:text-right' : 'md:text-left'}`}>
                      {service.count}
                    </span>

                    <div className={`w-20 h-20 mx-auto md:mx-0 ${service.side === 'right' ? 'md:ml-auto' : ''} mb-8 rounded-3xl bg-white shadow-2xl border border-slate-50 group-hover:border-[#D4AF37] flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_20px_40px_-10px_rgba(212,175,55,0.4)]`}>
                      <service.icon className="text-slate-800 group-hover:text-[#D4AF37] transition-colors" size={38} />
                    </div>

                    <h3 className="text-3xl font-bold text-slate-900 mb-5 group-hover:text-[#D4AF37] transition-colors tracking-tighter leading-tight">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-lg mb-4 font-medium opacity-80">{service.desc}</p>

                    {/* Connecting "Energy" Line */}
                    <div className={`hidden md:block absolute top-[60%] ${service.side === 'left' ? '-right-2' : '-left-2'} z-30`}>
                      <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        whileInView={{ width: "5rem", opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "backOut" }}
                        className={`h-1 rounded-full bg-gradient-to-r ${service.side === 'left' ? 'from-[#D4AF37] to-white/0 origin-left translate-x-full' : 'to-[#D4AF37] from-white/0 origin-right -translate-x-full'} shadow-[0_0_10px_rgba(212,175,55,0.5)]`}
                      />
                    </div>
                  </motion.div>

                  {/* Spacer / Optimized Ambient Decor (Fills the empty space) */}
                  <div className="hidden md:flex w-[46%] items-center justify-center relative select-none pointer-events-none">
                    <motion.div
                      animate={{
                        rotate: [0, 5, 0, -5, 0],
                        scale: [0.95, 1.05, 0.95]
                      }}
                      transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="absolute opacity-[0.04]"
                    >
                      <service.icon size={320} className="text-[#D4AF37]" strokeWidth={0.5} />
                    </motion.div>

                    {/* Extra Tech Detail Labels to fill space */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 0.1 }}
                      transition={{ delay: 1 }}
                      className="text-[4rem] font-black text-slate-400/20 uppercase tracking-[2rem] whitespace-nowrap rotate-90 absolute"
                    >
                      TECH SOLUTIONS
                    </motion.div>
                  </div>

                  {/* Glowing Node on the progressive center line */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1.2 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 300, delay: 0.3 }}
                    className="absolute left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-[4px] border-white bg-[#D4AF37] hidden md:block z-20 shadow-[0_0_20px_rgba(212,175,55,0.8)]"
                  >
                    <div className="w-full h-full rounded-full animate-ping bg-[#D4AF37] opacity-20"></div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision & Goal Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-slate-50/50 relative overflow-hidden border-b border-slate-100">
        {/* Subtle background tech accents */}
        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            {/* Vision Card - Light Premium */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, type: "spring" }}
              className="group relative bg-white p-12 lg:p-14 rounded-[3.5rem] border border-slate-100 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] hover:shadow-[#D4AF3710] transition-all duration-700 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-slate-50 rounded-bl-full -mr-10 -mt-10 group-hover:bg-[#D4AF37]/10 transition-colors duration-700"></div>

              <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center mb-10 shadow-2xl shadow-slate-200 group-hover:bg-[#D4AF37] transition-colors duration-500">
                <Globe className="text-[#D4AF37] group-hover:text-slate-900" size={36} />
              </div>

              <motion.span
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="text-[#D4AF37] font-bold tracking-widest uppercase text-xs mb-3 block"
              >
                The Future Focus
              </motion.span>
              <h3 className="text-4xl font-black text-slate-900 mb-8 tracking-tighter leading-tight group-hover:text-[#D4AF37] transition-colors duration-500">Our <span className="text-[#D4AF37] group-hover:text-slate-900">Vision</span></h3>

              <p className="text-xl text-slate-600 leading-relaxed font-semibold opacity-90 tracking-tight">
                To be the most trusted and preferred technology partner in the region, empowering every home and business with advanced security and technology solutions that simplify and secure their daily lives.
              </p>

              <div className="mt-10 flex items-center gap-2 text-slate-300 group-hover:text-[#D4AF37] transition-colors">
                <div className="h-0.5 w-12 bg-current"></div>
                <div className="h-0.5 w-2 bg-current"></div>
              </div>
            </motion.div>

            {/* Goal Card - Dark Professional */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, type: "spring", delay: 0.2 }}
              className="group relative bg-[#111827] p-12 lg:p-14 rounded-[3.5rem] border border-slate-800 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] hover:shadow-[#D4AF3720] transition-all duration-700 overflow-hidden"
            >
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-slate-800 rounded-tr-full -ml-10 -mb-10 group-hover:bg-[#D4AF37]/10 transition-colors duration-700"></div>

              <div className="w-20 h-20 bg-[#D4AF37] rounded-3xl flex items-center justify-center mb-10 shadow-2xl shadow-slate-950 group-hover:bg-white transition-colors duration-500">
                <Star className="text-slate-900" size={36} />
              </div>

              <motion.span
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="text-[#D4AF37] font-bold tracking-widest uppercase text-xs mb-3 block"
              >
                Our Commitment
              </motion.span>
              <h3 className="text-4xl font-black text-white mb-8 tracking-tighter leading-tight group-hover:text-[#D4AF37] transition-colors duration-500">Our <span className="text-[#D4AF37] group-hover:text-white">Goal</span></h3>

              <p className="text-xl text-slate-300 leading-relaxed font-semibold opacity-90 tracking-tight">
                Delivering high-quality services, genuine technological products, and 100% customer satisfaction through expert technical support and innovative, future-ready solutions for every single client.
              </p>

              <div className="mt-10 flex items-center gap-2 text-slate-700 group-hover:text-[#D4AF37] transition-colors">
                <div className="h-0.5 w-12 bg-current"></div>
                <div className="h-0.5 w-2 bg-current"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-[#020617] relative overflow-hidden">
        {/* Animated Background Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[120px] animate-pulse delay-1000"></div>

        {/* Tech decorative background grid */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="h-full w-full" style={{ backgroundImage: 'radial-gradient(#D4AF37 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {[
              { label: "Happy Customers", value: publicStats.customers, icon: Users, theme: "gold" },
              { label: "Projects Completed", value: publicStats.projects, icon: Briefcase, theme: "blue" },
              { label: "Expert Team", value: publicStats.team, icon: ShieldCheck, theme: "gold" }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.8, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                {/* Advanced Card Design */}
                <div className="relative p-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-[3rem] overflow-hidden">
                  {/* Glowing border effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/50 via-transparent to-blue-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                  <div className="relative bg-[#0f172a] p-10 rounded-[2.8rem] h-full flex flex-col items-center text-center">
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className={`w-20 h-20 rounded-3xl mb-8 flex items-center justify-center shadow-2xl relative overflow-hidden
                        ${stat.theme === 'gold' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-blue-500/10 text-blue-400'}`}
                    >
                      <stat.icon size={36} strokeWidth={1.5} />
                      <div className="absolute inset-0 bg-white/5 group-hover:translate-y-full transition-transform duration-700"></div>
                    </motion.div>

                    <div className="space-y-3">
                      <h4 className="text-6xl font-black tracking-tighter flex items-center justify-center">
                        <span className={`bg-clip-text text-transparent bg-gradient-to-b ${stat.theme === 'gold' ? 'from-white via-white to-[#D4AF37]' : 'from-white via-white to-blue-400'}`}>
                          <Counter value={stat.value} />
                        </span>
                      </h4>
                      <div className="h-px w-12 bg-slate-800 mx-auto group-hover:w-24 group-hover:bg-[#D4AF37] transition-all duration-500"></div>
                      <p className="text-slate-400 font-bold uppercase tracking-[0.3em] text-[11px] group-hover:text-white transition-colors">
                        {stat.label}
                      </p>
                    </div>

                    {/* Bottom accent glow */}
                    <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-1 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500
                      ${stat.theme === 'gold' ? 'bg-[#D4AF37]' : 'bg-blue-400'}`}></div>
                  </div>
                </div>

                {/* Background Shadow Glow */}
                <div className={`absolute -inset-4 blur-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-700 -z-10
                  ${stat.theme === 'gold' ? 'bg-[#D4AF37]' : 'bg-blue-500'}`}></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Surveillance Highlight Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjY3R2JTIwc2VjdXJpdHklMjBzeXN0ZW18ZW58MHx8fHwxNzYzMzAxODUwfDA&ixlib=rb-4.1.0&q=85"
                alt="Advanced Surveillance"
                className="rounded-[3rem] shadow-2xl relative z-10 border border-slate-100"
              />
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-10 -right-10 bg-slate-950 text-white p-8 rounded-3xl shadow-2xl z-20 border border-slate-800"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center">
                    <ShieldCheck className="text-slate-900" size={28} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest">Active Protection</p>
                    <p className="text-2xl font-black text-white">24/7 Monitoring</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed max-w-[200px]">Advanced AI-driven surveillance for absolute peace of mind.</p>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs">Flagship Service</span>
                <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">Elite <span className="text-[#D4AF37]">Security & Surveillance</span> Solutions.</h2>
                <p className="text-xl text-slate-600 font-medium leading-relaxed">
                  We don't just sell cameras; we engineer complete security ecosystems. From high-definition IP cameras to AI-powered intrusion detection, we keep you safe.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { title: "Smart IP Cameras", desc: "4K Resolution & Night Vision" },
                  { title: "Mobile Access", desc: "Monitor from anywhere" },
                  { title: "AI Analytics", desc: "Human & Vehicle Detection" },
                  { title: "Cloud Backup", desc: "Secure remote storage" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 group">
                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                      <CheckCircle className="text-[#D4AF37] group-hover:text-white" size={18} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6">
                <Button size="lg" onClick={() => navigate('/services/security-surveillance')} className="bg-slate-900 hover:bg-slate-800 text-white px-8 h-14 rounded-2xl font-bold flex items-center gap-3">
                  View Security Details <ArrowRight size={20} />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/90 backdrop-blur-lg border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Client Reviews</h2>
              <p className="text-lg text-slate-600">What our customers say about us</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 ">
              {reviews.map((review) => (
                <Card key={review.id} className="border-0 shadow-lg bg-slate-50 border border-[#D4AF37] rounded-xl" data-testid={`review-card-${review.id}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg">{review.customer_name}</CardTitle>
                      <div className="flex">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={16} fill="#FFA500" color="#FFA500" />
                        ))}
                      </div>
                    </div>
                    <CardDescription className="text-sm">{review.service_taken}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">{review.review_text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button size="lg" onClick={() => navigate('/reviews')} data-testid="view-all-reviews-btn">View All Reviews</Button>
            </div>
          </div>
        </section>
      )}

      {/* Global Footer CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative bg-slate-950 rounded-[4rem] p-12 sm:p-20 text-center overflow-hidden shadow-2xl group border border-white/5"
          >
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
            <div className="relative z-10 space-y-8">
              <h2 className="text-4xl sm:text-6xl font-black text-white tracking-tighter leading-tight">
                Ready to <span className="text-[#D4AF37]">Secure</span> Your Premises?
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
                Get a customized security quote tailored to your specific requirements.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6 pt-6">
                <Button
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-16 px-10 rounded-2xl text-xl font-bold transition-all hover:scale-105 shadow-[0_0_40px_rgba(212,175,55,0.3)] flex items-center gap-3 group"
                >
                  Contact Our Security Experts
                  <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const AdminLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/local-login`,
        { username, password },
        { withCredentials: true }
      );

      // Immediately assume authenticated
      setUser({ username }); // simple mark as logged in

      toast.success("Login Successful!");
      navigate("/admin"); // redirect instantly

    } catch (err) {
      toast.error("Invalid Credentials");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md p-8 bg-slate-50 border border-[#D4AF37] rounded-xl hover: transition-all">
        <h2 className="text-2xl font-bold mb-6">Admin Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>{loading ? 'Logging in...' : 'Login'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const About = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-testid="about-page">
      {/* Background Subtle Mesh decoration */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '48px 48px' }}></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h1 className="text-6xl sm:text-7xl font-bold text-slate-900 tracking-tighter">Redefining Tech Solutions in <span className="text-[#D4AF37]">Kumbakonam.</span></h1>
            <p className="text-xl text-slate-500 italic font-medium tracking-wide">
              At V4 TECH, we combine expert <span className="text-slate-900 font-bold">hardware engineering</span> with modern <span className="text-slate-900 font-bold">digital solutions</span> to keep your home and business secure and efficient.
            </p>
          </motion.div>
        </div>



        {/* Who We Are Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-32 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative group"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-[#D4AF37]/20 to-transparent rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <img
              src="https://images.unsplash.com/photo-1712159018726-4564d92f3ec2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwY29tcGFueSUyMG9mZmljZXxlbnwwfHx8fDE3NjMzMDE4NTB8MA&ixlib=rb-4.1.0&q=85"
              alt="V4 Tech Enterprise"
              className="relative z-10 rounded-3xl shadow-2xl w-full h-[450px] object-cover border border-slate-100 transform transition-transform duration-500 group-hover:scale-[1.02]"
            />
            {/* Floating Badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -right-6 z-20 bg-[#D4AF37] text-white px-6 py-4 rounded-2xl shadow-xl font-bold"
            >
              8+ Years Experience
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center space-y-8"
          >
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter italic border-l-4 border-[#D4AF37] pl-8 uppercase">Who We Are</h2>
            <div className="space-y-6">
              <p className="text-xl font-bold text-slate-900 leading-relaxed italic">
                V4 TECH ENTERPRISE started with a simple mission: to provide high-quality, reliable technology services that people can trust.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Based in the heart of <span className="text-[#D4AF37] font-bold underline decoration-slate-200 decoration-2 underline-offset-4">Natchiyar Kovil, Kumbakonam</span>, we have grown from a local service center into a premium technology partner.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Whether it’s securing your property with advanced <span className="text-slate-900 font-bold">CCTV</span> or reviving a slow laptop, our team of <span className="text-slate-900 font-bold">8+ experts</span> is dedicated to delivering <span className="text-[#D4AF37] font-semibold">precision and excellence</span>.
              </p>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-xl text-slate-900 font-black tracking-tight"
              >
                We don't just fix devices; we build future-ready infrastructure.
              </motion.p>
            </div>
          </motion.div>
        </div>

        {/* Our Specialized Expertise Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our Specialized <span className="text-[#D4AF37]">Expertise</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { id: 1, title: "Digital Security", icon: Shield, delay: 0.1, desc: "End-to-end CCTV installation with remote mobile access so you can monitor your property from anywhere." },
              { id: 2, title: "Computing Solutions", icon: Cpu, delay: 0.2, desc: "Expert chip-level service for laptops/PCs and custom system builds tailored for gaming or professional work." },
              { id: 3, title: "Network Infrastructure", icon: Globe, delay: 0.3, desc: "Seamless Wi-Fi and networking setups for homes and large office spaces." }
            ].map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: item.delay, duration: 0.5 }}
                whileHover={{ scale: 1.05, translateY: -10 }}
                className="p-8 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-2xl transition-all cursor-default group"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="p-5 bg-slate-50 rounded-2xl mb-6 group-hover:bg-[#D4AF37] group-hover:text-white transition-all duration-300 transform group-hover:rotate-12">
                    <item.icon className="text-[#D4AF37] group-hover:text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Core Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="bg-slate-50 border border-slate-200 rounded-[4rem] p-16 sm:p-24 mb-16 text-slate-900 relative overflow-hidden shadow-sm"
        >
          <div className="absolute top-0 right-0 p-12 opacity-[0.03]">
            <Rocket size={400} className="text-slate-900" />
          </div>
          <h2 className="text-4xl font-extrabold mb-16 text-center tracking-tight uppercase">Our <span className="text-[#D4AF37]">Core Values</span></h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10">
            {[
              { icon: ShieldCheck, title: "Genuine Parts", desc: <>We only use <span className="text-slate-900 font-bold">100% original</span> engineering materials and spare parts. No compromises.</> },
              { icon: Users, title: "Customer First", desc: <>With <span className="text-slate-900 font-bold">207+ happy customers</span>, our priority is solving your problem at the source.</> },
              { icon: Clock, title: "Fast Support", desc: <>Technology waits for no one. We ensure <span className="text-slate-900 font-bold">quick response times</span> and efficient hardware optimization.</> }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="p-8 bg-white rounded-3xl border border-slate-100 shadow-sm transition-all hover:shadow-xl text-center group"
              >
                <div className="w-20 h-20 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-8 transition-all group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] group-hover:text-white">
                  <value.icon className="text-[#D4AF37] group-hover:text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 tracking-tight">{value.title}</h3>
                <div className="text-slate-600 leading-relaxed font-medium">{value.desc}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust Signals Section */}
        <div className="mt-20 flex flex-wrap justify-center gap-8 border-t border-slate-100 pt-12">
          <div className="flex items-center gap-3 px-6 py-3 bg-white border border-slate-200 rounded-full shadow-sm hover:border-[#D4AF37] transition-colors group">
            <Award className="text-[#D4AF37] group-hover:scale-110 transition-transform" size={24} />
            <span className="text-sm font-bold text-slate-700 tracking-tight">Authorized Service Partner</span>
          </div>
          <div className="flex items-center gap-3 px-6 py-3 bg-white border border-slate-200 rounded-full shadow-sm hover:border-[#D4AF37] transition-colors group">
            <ShieldCheck className="text-[#D4AF37] group-hover:scale-110 transition-transform" size={24} />
            <span className="text-sm font-bold text-slate-700 tracking-tight">ISO Certified Security</span>
          </div>
          <div className="flex items-center gap-3 px-6 py-3 bg-white border border-slate-200 rounded-full shadow-sm hover:border-[#D4AF37] transition-colors group">
            <Users className="text-[#D4AF37] group-hover:scale-110 transition-transform" size={24} />
            <span className="text-sm font-bold text-slate-700 tracking-tight">Certified System Engineers</span>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <motion.a
            href="https://wa.me/919442043644"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-4 bg-[#D4AF37] hover:bg-slate-900 text-white px-10 py-5 rounded-full text-lg font-bold shadow-2xl transition-all duration-300 group"
          >
            <MessageCircle className="group-hover:rotate-12 transition-transform" size={28} />
            Have a Tech Issue? Talk to our Expert
          </motion.a>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mt-6 text-slate-500 font-bold uppercase tracking-widest text-xs"
          >
            Instant Support & Guaranteed Solutions
          </motion.p>
        </div>
      </div>

      {/* Full-width Service Ticker / Marquee Section (Light Theme) */}
      <div className="mt-32 w-full bg-white border-y border-slate-100 overflow-hidden py-12 relative">
        <motion.div
          animate={{ x: [0, -1500] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="flex whitespace-nowrap gap-16 items-center"
        >
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              <span className="text-5xl font-black text-transparent uppercase tracking-tighter" style={{ WebkitTextStroke: '1px #94a3b8' }}>CCTV</span>
              <span className="text-5xl font-black text-[#D4AF37] uppercase tracking-tighter">Networking</span>
              <span className="text-5xl font-black text-transparent uppercase tracking-tighter" style={{ WebkitTextStroke: '1px #94a3b8' }}>IP Cameras</span>
              <span className="text-5xl font-black text-slate-900 uppercase tracking-tighter">System Customization</span>
              <span className="text-5xl font-black text-transparent uppercase tracking-tighter" style={{ WebkitTextStroke: '1px #94a3b8' }}>Sales & Service</span>
            </div>
          ))}
        </motion.div>
        {/* Subtle gradient edges for better fade effect */}
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent z-10"></div>
      </div>
    </div>
  );
};



const BookService = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    service_needed: "",
    description: "",
    image_data: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/customer-details`, formData);
      toast.success("Submitted Successfully! Our team will contact you within 24 hours.", {
        duration: 5000,
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '16px',
          padding: '16px 24px',
          borderRadius: '12px'
        }
      });
      setFormData({
        name: "",
        address: "",
        email: "",
        phone: "",
        service_needed: "",
        description: "",
        image_data: ""
      });
      setTimeout(() => navigate('/'), 2500);
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8" data-testid="book-service-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Book a Service</h1>
          <p className="text-lg text-slate-700">Fill out the form and we'll get back to you within 24 hours</p>
        </div>

        <Card className="bg-slate-50 border border-slate-200 rounded-xl shadow-2xl">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="book-name-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="book-address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="book-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="book-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="service">Service Needed *</Label>
                <Select value={formData.service_needed} onValueChange={(value) => setFormData({ ...formData, service_needed: value })}>
                  <SelectTrigger data-testid="service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-50 border border-slate-200 rounded-xl shadow-2xl" >
                    {/* Security & Surveillance */}
                    <SelectItem value="CCTV Installation">🛡️ CCTV Installation</SelectItem>
                    <SelectItem value="CCTV Maintenance">🛡️ CCTV Maintenance & Repair</SelectItem>
                    <SelectItem value="AI Camera Setup">🛡️ AI-Powered Camera Setup</SelectItem>
                    {/* Computer & Laptop */}
                    <SelectItem value="Laptop Repair">💻 Laptop Repair</SelectItem>
                    <SelectItem value="Desktop Repair">💻 Desktop / PC Repair</SelectItem>
                    <SelectItem value="Chip-Level Repair">💻 Chip-Level Board Repair</SelectItem>
                    <SelectItem value="Software Service">💻 OS Install / Software Fix</SelectItem>
                    {/* Networking */}
                    <SelectItem value="WiFi Setup">🌐 WiFi / Router Setup</SelectItem>
                    <SelectItem value="Network Infrastructure">🌐 Office Network Infrastructure</SelectItem>
                    {/* System Customization */}
                    <SelectItem value="Custom PC Build">⚙️ Custom PC Build</SelectItem>
                    <SelectItem value="Hardware Upgrade">⚙️ Hardware Upgrade</SelectItem>
                    {/* Sales & Retail */}
                    <SelectItem value="Laptop Purchase">🛒 Laptop / Desktop Purchase</SelectItem>
                    <SelectItem value="CCTV Kit Purchase">🛒 CCTV Kit Purchase</SelectItem>
                    <SelectItem value="Accessories Purchase">🛒 Accessories & Peripherals</SelectItem>
                    {/* Mobile Solutions */}
                    <SelectItem value="Mobile Screen Repair">📱 Mobile Screen Replacement</SelectItem>
                    <SelectItem value="Mobile Chip Repair">📱 Mobile Chip-Level Repair</SelectItem>
                    <SelectItem value="Mobile Data Recovery">📱 Mobile Data Recovery</SelectItem>
                    <SelectItem value="Mobile Battery Replace">📱 Battery Replacement</SelectItem>
                    <SelectItem value="Mobile Water Damage">📱 Water Damage Recovery</SelectItem>
                    {/* Other */}
                    <SelectItem value="Other">Other / General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description of Issue/Requirements *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  required
                  data-testid="book-description-input"
                />
              </div>

              <div>
                <Label htmlFor="image">Upload Image (Optional)</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  data-testid="book-image-input"
                />
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="submit-booking-btn">
                {submitting ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    service_taken: "",
    rating: 5,
    review_text: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success("Thank you for your review!.");
      setFormData({
        customer_name: "",
        email: "",
        service_taken: "",
        rating: 5,
        review_text: ""
      });
      setShowForm(false);
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Customer Reviews</h1>
          <p className="text-lg text-slate-700 mb-8">See what our clients have to say</p>
          <Button size="lg" onClick={() => setShowForm(!showForm)} data-testid="write-review-btn">
            {showForm ? "Cancel" : "Write a Review"}
          </Button>
        </div>

        {showForm && (
          <Card className="max-w-3xl mx-auto mb-12 bg-slate-50 border border-slate-200 rounded-xl shadow-2xl" data-testid="review-form">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="customer_name">Your Name *</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      data-testid="review-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="review-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service_taken">Service Taken *</Label>
                  <Select value={formData.service_taken} onValueChange={(value) => setFormData({ ...formData, service_taken: value })}>
                    <SelectTrigger data-testid="review-service-select">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-50 border border-slate-200">
                      <SelectItem value="CCTV Installation">CCTV Installation</SelectItem>
                      <SelectItem value="Laptop Service">Laptop Service</SelectItem>
                      <SelectItem value="Website Development">Website Development</SelectItem>
                      <SelectItem value="Others Services">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Rating *</Label>
                  <div className="flex gap-2 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        data-testid={`rating-${star}`}
                      >
                        <Star
                          size={32}
                          fill={star <= formData.rating ? "#FFA500" : "none"}
                          color={star <= formData.rating ? "#FFA500" : "#gray"}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="review_text">Your Review *</Label>
                  <Textarea
                    id="review_text"
                    value={formData.review_text}
                    onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                    rows={5}
                    required
                    data-testid="review-text-input"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="submit-review-btn">
                  {submitting ? "Submitting..." : "Submit Review"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.map((review) => (
            <Card key={review.id} className="bg-slate-50 border border-slate-200 rounded-xl shadow-lg" data-testid={`review-card-${review.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle>{review.customer_name}</CardTitle>
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#FFA500" color="#FFA500" />
                    ))}
                  </div>
                </div>
                <CardDescription>{review.service_taken}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{review.review_text}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {reviews.length === 0 && !showForm && (
          <div className="text-center py-16">
            <p className="text-xl text-gray-600">No reviews yet. Be the first to review!</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Support = () => {
  const [activeTab, setActiveTab] = useState("submit");
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    subject: "",
    description: ""
  });
  const [searchId, setSearchId] = useState("");
  const [complaint, setComplaint] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/complaints`, formData);
      toast.success(`Submitted Successfully! Our team will contact you within 24 hours. Your Complaint ID is: ${response.data.complaint_id}`, {
        duration: 6000,
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '16px',
          padding: '16px 24px',
          borderRadius: '12px'
        }
      });
      setFormData({
        customer_name: "",
        email: "",
        phone: "",
        subject: "",
        description: ""
      });
      setActiveTab("check");
      setSearchId(response.data.complaint_id);
    } catch (error) {
      toast.error("Failed to submit complaint. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setComplaint(null);

    try {
      const response = await axios.get(`${API}/complaints/search/${searchId}`);
      setComplaint(response.data);
    } catch (error) {
      toast.error("Complaint not found. Please check your Complaint ID or Phone Number.");
    } finally {
      setSearching(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending": return "text-yellow-600 bg-yellow-50";
      case "In Progress": return "text-blue-600 bg-blue-50";
      case "Completed": return "text-green-600 bg-green-50";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8" data-testid="support-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Customer Support</h1>
          <p className="text-lg text-slate-700">Submit a complaint or check your complaint status</p>
        </div>

        <div className="flex justify-center gap-4 mb-8">
          <Button
            variant={activeTab === "submit" ? "default" : "outline"}
            onClick={() => setActiveTab("submit")}
            data-testid="submit-tab-btn"
          >
            Submit Complaint
          </Button>
          <Button
            variant={activeTab === "check" ? "default" : "outline"}
            onClick={() => setActiveTab("check")}
            data-testid="check-tab-btn"
          >
            Check Status
          </Button>
        </div>

        {activeTab === "submit" && (
          <Card className="bg-slate-50 border border-slate-200 rounded-xl shadow-2xl" data-testid="complaint-form">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="customer_name">Full Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                    data-testid="complaint-name-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="complaint-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="complaint-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    data-testid="complaint-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    required
                    data-testid="complaint-description-input"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="submit-complaint-btn">
                  {submitting ? "Submitting..." : "Submit Complaint"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === "check" && (
          <div className="space-y-8">
            <Card className="bg-slate-50 border border-slate-200 rounded-xl shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSearch} className="space-y-6">
                  <div>
                    <Label htmlFor="searchId">Enter Phone Number or Complaint ID</Label>
                    <Input
                      id="searchId"
                      value={searchId}
                      onChange={(e) => setSearchId(e.target.value)}
                      placeholder="e.g., 9876543210"
                      required
                      data-testid="search-complaint-input"
                    />
                    <p className="text-sm text-slate-700 mt-1">You can search using Phone Number or Complaint ID</p>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={searching} data-testid="search-complaint-btn">
                    {searching ? "Searching..." : "Check Status"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {complaint && (
              <Card className=" bg-slate-50 border border-slate-200 border-0 shadow-2xl" data-testid="complaint-details">
                <CardHeader>
                  <CardTitle className="text-2xl">Complaint Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-900">Complaint ID</p>
                    <p className="text-lg font-semibold">{complaint.complaint_id}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-900">Status</p>
                    <span className={`inline-block px-4 py-2 rounded-full font-semibold ${getStatusColor(complaint.status)}`}>
                      {complaint.status}
                    </span>
                  </div>

                  <div>
                    <p className="text-sm text-slate-900">Subject</p>
                    <p className="text-lg">{complaint.subject}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-900">Description</p>
                    <p className="text-slate-600">{complaint.description}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-900">Submitted</p>
                      <p className="text-slate-600">{new Date(complaint.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900">Last Updated</p>
                      <p className="text-slate-600">{new Date(complaint.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


const SecuritySurveillanceV2 = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white pt-24 pb-20" data-testid="security-page">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none bg-slate-900" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${cctvimage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/70 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
      </div>

      {/* ── Cinematic Hero ── */}
      <section className="relative py-28 mb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-[#D4AF37] text-sm font-bold tracking-widest uppercase mb-6">
            <Shield size={16} /> Security &amp; Surveillance
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter">
            360° Protection for Your <span className="text-[#D4AF37]">Home &amp; Business.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
            Advanced IP &amp; Analog Surveillance Systems with AI-Powered Monitoring and Cloud Integration.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/contact')} className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-14 px-8 text-lg font-bold">
              Get a Security Quote <ArrowRight className="ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => window.open('https://wa.me/919025338807', '_blank')} className="border-white/20 text-white hover:bg-white/10 h-14 px-8 text-lg font-bold">
              Live Demo Request
            </Button>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Next-Generation <span className="text-[#D4AF37]">Security Architecture</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
            <p className="mt-8 text-xl text-slate-700 font-semibold max-w-4xl mx-auto">
              At V4 TECH, we don't just install cameras; we design comprehensive security ecosystems. We utilize industry-leading hardware and advanced networking protocols to ensure your property is protected 24/7.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Smart IP Surveillance",
                desc: "Ultra-HD 4K resolution cameras powered by PoE (Power over Ethernet) for stable, high-bandwidth digital feeds.",
                icon: Video
              },
              {
                title: "AI & Analytics",
                desc: "Motion detection, human/vehicle classification, and perimeter protection (Line Crossing & Intrusion detection).",
                icon: Cpu
              },
              {
                title: "Low-Light Mastery",
                desc: "Implementation of Full-Color Night Vision and Infrared (IR) technology for crystal-clear imaging in zero-light conditions.",
                icon: Eye
              },
              {
                title: "H.265+ Video Compression",
                desc: "Advanced encoding that reduces bandwidth usage and saves up to 80% of HDD storage space without compromising video quality.",
                icon: HardDrive
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] hover:shadow-xl transition-all group"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <item.icon className="text-[#D4AF37] group-hover:text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm font-medium">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase"><span className="text-[#D4AF37]">Specialized Solutions</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Residential Security */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mb-8">
                <HomeIcon className="text-[#D4AF37]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 underline decoration-[#D4AF37] underline-offset-8">1. Residential Security</h3>
              <p className="text-slate-600 mb-8 font-medium italic">Protect your family and assets with smart home monitoring.</p>
              <ul className="space-y-4">
                {[
                  { label: "Two-Way Audio", desc: "Communicate through your cameras via smartphone." },
                  { label: "Instant Mobile Alerts", desc: "Receive push notifications the moment motion is detected." },
                  { label: "Discreet Installation", desc: "Aesthetic wiring and placement to preserve your home’s interior." }
                ].map((point, idx) => (
                  <li key={idx} className="flex flex-col gap-1">
                    <span className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {point.label}
                    </span>
                    <span className="text-slate-500 text-sm pl-3.5 leading-relaxed">{point.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Enterprise & Industrial */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-bl-full"></div>
              <div className="w-16 h-16 bg-[#D4AF37] rounded-2xl flex items-center justify-center mb-8">
                <Building2 className="text-slate-900" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-6 underline decoration-[#D4AF37] underline-offset-8">2. Enterprise Solutions</h3>
              <p className="text-white/80 mb-8 font-medium italic text-sm">Scalable solutions for warehouses, retail outlets, and corporate offices.</p>
              <ul className="space-y-4">
                {[
                  { label: "Centralized Management (CMS)", desc: "Monitor multiple branches from a single control room." },
                  { label: "Redundant Storage", desc: "RAID configurations to ensure zero data loss." },
                  { label: "High-Capacity NVRs", desc: "Support for 16, 32, and 64+ channel systems." }
                ].map((point, idx) => (
                  <li key={idx} className="flex flex-col gap-1 text-left">
                    <span className="font-bold text-white text-sm flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {point.label}
                    </span>
                    <span className="text-white/70 text-sm pl-3.5 leading-relaxed">{point.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Remote Cloud Access */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-8">
                <Globe className="text-blue-500" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 underline decoration-blue-500 underline-offset-8">3. Remote Cloud Access</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                View your premises from anywhere in the world. We configure secure <strong>P2P Cloud Tunnels</strong> that allow you to stream live footage and playback recordings directly on your iOS or Android devices.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Hardware Standards Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Hardware Standards</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Premium Components for <span className="text-[#D4AF37]">Maximum Reliability</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
            <p className="mt-8 text-xl text-slate-700 font-semibold max-w-4xl mx-auto">
              We exclusively use surveillance-grade hardware to ensure long-term durability and performance.
            </p>
          </motion.div>

          <div className="bg-white rounded-[3rem] border border-slate-100 shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="py-6 px-8 text-sm font-bold uppercase tracking-widest border-b border-slate-800">Component</th>
                    <th className="py-6 px-8 text-sm font-bold uppercase tracking-widest border-b border-slate-800">Specifications</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    { component: "Trusted Brands", specs: "Authorized Partner for Hikvision, Dahua, and CP Plus" },
                    { component: "Storage Media", specs: "Specialized WD Purple / Seagate Skyhawk Surveillance HDDs" },
                    { component: "Cabling Infrastructure", specs: "Pure Copper CAT6 / Coaxial cables for zero signal attenuation" },
                    { component: "Power Protection", specs: "Stabilized SMPS with built-in Surge & Lightning Protection" }
                  ].map((row, idx) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="group hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-6 px-8 font-bold text-slate-900 border-r border-slate-50 group-hover:text-[#D4AF37] transition-colors">{row.component}</td>
                      <td className="py-6 px-8 text-slate-600 font-medium italic">{row.specs}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Why V4 TECH Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Our Commitment</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Why <span className="text-[#D4AF37]">V4 TECH?</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {[
              {
                title: "Zero Blind-Spot Coverage",
                desc: "Professional site mapping to ensure every single corner of your property is monitored with precision.",
                icon: Map
              },
              {
                title: "Secure Networking",
                desc: "Hardened DVR/NVR configurations to prevent unauthorized digital access and protect your privacy.",
                icon: ShieldAlert
              },
              {
                title: "On-Site & Remote Support",
                desc: "Rapid troubleshooting for configuration issues, playback assistance, and routine hardware health checks.",
                icon: Headphones
              },
              {
                title: "Scalable Frameworks",
                desc: "We design and install your wiring infrastructure today so you can easily add more cameras as you grow.",
                icon: Layout
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative p-10 bg-white border border-slate-100 rounded-[3rem] hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#D4AF37]/5 rounded-bl-[4rem] group-hover:scale-150 transition-transform duration-700"></div>
                <div className="flex items-start gap-6 relative z-10">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center shrink-0 shadow-lg group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <item.icon className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Our Process Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Execution Strategy</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our <span className="text-[#D4AF37]">Security Protocol</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "Detailed on-site assessment to identify security vulnerabilities." },
              { step: "02", stepLabel: "Design", title: "System Architecture", desc: "Mapping camera placements and network topography for maximum coverage." },
              { step: "03", title: "Installation", desc: "Professional implementation with precision wiring and hardware mounting." },
              { step: "04", title: "Optimization", desc: "Fine-tuning AI analytics, cloud setup, and user training for seamless operation." }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative group text-center"
              >
                <div className="text-6xl font-black text-slate-100 group-hover:text-[#D4AF3715] transition-colors mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm font-medium leading-relaxed">{item.desc}</p>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-8 -right-4 w-8 h-px bg-slate-200" />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-slate-950 rounded-[4rem] p-12 sm:p-20 text-center overflow-hidden shadow-2xl group border border-white/5"
        >
          {/* Advanced Background Decoration */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,#D4AF3715_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>

          <div className="relative z-10 space-y-8">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="inline-flex items-center gap-3 px-6 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full text-[#D4AF37] text-sm font-bold uppercase tracking-widest"
            >
              <Shield size={16} /> Secure Your Premises
            </motion.div>

            <h2 className="text-4xl sm:text-6xl font-black text-white tracking-tighter leading-tight">
              Ready to <span className="text-[#D4AF37]">Secure</span> Your Premises?
            </h2>

            <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
              Get a customized security quote tailored to your specific requirements.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-6">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-16 px-10 rounded-2xl text-xl font-bold transition-all hover:scale-105 shadow-[0_0_40px_rgba(212,175,55,0.3)] flex items-center gap-3 group"
              >
                Contact Our Security Experts
                <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('https://wa.me/919025338807', '_blank')}
                className="border-white/20 text-white hover:bg-white/10 h-16 px-10 rounded-2xl text-xl font-bold transition-all flex items-center gap-3"
              >
                <Video size={20} className="text-[#D4AF37]" />
                Live Demo Request
              </Button>
            </div>
          </div>

          {/* Floating Security Icon Background */}
          <div className="absolute -right-16 -bottom-16 opacity-[0.03] text-[#D4AF37] -rotate-12 pointer-events-none">
            <ShieldAlert size={400} strokeWidth={0.5} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const ComputerLaptopService = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-24 bg-white relative overflow-hidden" data-testid="laptop-service-page">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none bg-slate-950" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${hardwareimg})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/80 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
      </div>

      {/* Hero Content Section */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full py-20 mb-24">
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-900 border border-[#D4AF37]/30 text-[#D4AF37] rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-4 shadow-xl">
              <Cpu size={14} className="animate-pulse" /> Precision Hardware Engineering
            </div>
            
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-tight drop-shadow-2xl">
              Precision Repairs & <span className="text-[#D4AF37]">High-Performance</span> Computing.
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-100 max-w-4xl mx-auto font-medium leading-relaxed italic drop-shadow-md">
              From Micro-Soldering to System Optimization—We Revive Your Tech with Genuine Parts and Expert Engineering.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-6">
              <Button size="lg" onClick={() => navigate('/book-service')} className="bg-[#D4AF37] hover:bg-white text-black px-10 h-14 rounded-2xl font-bold shadow-lg shadow-[#D4AF3740] transition-all">
                Book a Repair
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.open('https://wa.me/919025338807', '_blank')} className="border-white/30 text-white hover:bg-white hover:text-black px-10 h-14 rounded-2xl font-bold transition-all bg-white/20 backdrop-blur-sm">
                WhatsApp Expert
              </Button>
            </div>
          </motion.div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" >

        {/* Core Expertise Section */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our <span className="text-[#D4AF37]">Core Expertise</span></h2>
                <h4 className="text-2xl font-bold text-slate-800 tracking-tight">Specialized Hardware & Software Solutions</h4>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                At V4 TECH, we don't just "fix" computers; we restore them to factory-grade performance. Our lab is equipped with advanced diagnostic tools to handle complex chip-level repairs and software architecture issues.
              </p>
              <div className="pt-4 grid grid-cols-2 gap-8">
                <div className="border-l-2 border-[#D4AF37] pl-6 py-2">
                  <p className="text-3xl font-black text-slate-900">100%</p>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Genuine Parts</p>
                </div>
                <div className="border-l-2 border-slate-900 pl-6 py-2">
                  <p className="text-3xl font-black text-slate-900">Lab</p>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Equipped Facility</p>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 gap-6">
              {[
                {
                  title: "Logic Board & Chip-Level Repair",
                  desc: "Expertise in SMD (Surface Mount Device) soldering, IC replacements, and power circuit troubleshooting for dead laptops.",
                  icon: Cpu
                },
                {
                  title: "Thermal Management Optimization",
                  desc: "Professional internal cleaning and application of high-grade Thermal Grizzly / Arctic Silver thermal paste to prevent CPU/GPU throttling.",
                  icon: Thermometer
                },
                {
                  title: "Display & Panel Replacement",
                  desc: "High-quality IPS/OLED screen replacements for laptops with zero-dead-pixel guarantees.",
                  icon: Monitor
                },
                {
                  title: "Data Recovery & Security",
                  desc: "Advanced recovery from corrupted HDDs, SSDs, and NVMe drives, along with professional malware/ransomware removal.",
                  icon: Database
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] hover:border-[#D4AF3750] hover:bg-white hover:shadow-2xl transition-all group"
                >
                  <div className="w-14 h-14 bg-white shadow-sm flex items-center justify-center shrink-0 rounded-2xl group-hover:bg-[#D4AF37] transition-colors">
                    <item.icon className="text-[#D4AF37] group-hover:text-white" size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#D4AF37] transition-colors">{item.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Performance Engineering Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Performance Lab</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Extreme <span className="text-[#D4AF37]">Performance Upgrades</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                title: "Gaming PC Optimization",
                desc: "Custom overclocking, thermal liquid metal application, and airflow engineering for high-FPS gaming parity.",
                icon: Rocket,
                tags: ["No Lag", "Max FPS"]
              },
              {
                title: "Workstation Build",
                desc: "Architectural design for video editing, 3D rendering, and AI development workstations with certified hardware.",
                icon: Zap,
                tags: ["NVIDIA RTX", "Ryzen/Intel"]
              },
              {
                title: "Server & NAS Setup",
                desc: "Implementation of secure local cloud storage and high-availability server clusters for SMEs.",
                icon: Globe,
                tags: ["Local Cloud", "Raid 5"]
              }
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-10 bg-slate-50/50 border border-slate-100 rounded-[3.5rem] hover:bg-white hover:shadow-2xl transition-all duration-500"
              >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm group-hover:bg-slate-900 transition-colors">
                  <service.icon className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                <p className="text-slate-600 mb-8 font-medium leading-relaxed text-sm">{service.desc}</p>
                <div className="flex gap-2">
                  {service.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border border-slate-200 px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Performance Upgrades Section */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Optimization Lab</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Performance <span className="text-[#D4AF37]">Upgrades</span></h2>
            <h4 className="text-xl font-bold text-slate-800 tracking-tight mt-4 italic underline decoration-[#D4AF37] decoration-2 underline-offset-8">Don’t Replace—Upgrade Your Workflow</h4>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-8 rounded-full"></div>
            <p className="mt-8 text-xl text-slate-700 font-semibold max-w-3xl mx-auto">
              Boost your existing machine’s speed and efficiency with our strategic upgrade packages.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                title: "Storage Evolution",
                desc: "Transition from mechanical HDDs to high-speed NVMe/SATA SSDs for 10x faster boot times and app loading.",
                icon: HardDrive,
                tags: ["10x Speed", "NVMe/SATA"]
              },
              {
                title: "Memory Expansion",
                desc: "Dual-channel DDR4/DDR5 RAM upgrades to handle heavy multitasking, coding environments, and 4K video editing.",
                icon: Cpu,
                tags: ["DDR4/DDR5", "No Lag"]
              },
              {
                title: "OS Deployment",
                desc: "Clean installations of Windows 11, macOS, or Linux (Ubuntu/Kali) with optimized driver configurations and security patches.",
                icon: Laptop,
                tags: ["Secure", "Optimized"]
              }
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-10 bg-white border border-slate-100 rounded-[3rem] hover:shadow-2xl transition-all duration-500 overflow-hidden relative"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 rounded-bl-[4rem] group-hover:bg-[#D4AF37]/10 transition-colors"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mb-8 shadow-lg group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <service.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-[#D4AF37] transition-colors">{service.title}</h3>
                  <p className="text-slate-600 mb-8 font-medium leading-relaxed text-sm">{service.desc}</p>
                  <div className="flex gap-2">
                    {service.tags.map((tag, tIdx) => (
                      <span key={tIdx} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border border-slate-200 px-3 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Brand Coverage Section */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Brand Coverage</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">We Service <span className="text-[#D4AF37]">All Major Manufacturers</span></h2>
            <p className="mt-8 text-xl text-slate-700 font-semibold max-w-4xl mx-auto italic">
              Our technicians are trained to handle premium and enterprise-grade hardware including:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                brand: "Apple Ecosystem",
                desc: "MacBook Pro / Air (Intel & M-Series) Logic Board & Battery Services.",
                icon: Laptop,
                color: "bg-slate-50"
              },
              {
                brand: "Gaming Performance",
                desc: "Alienware, ROG, MSI, and Legion high-performance maintenance.",
                icon: Zap,
                color: "bg-slate-50"
              },
              {
                brand: "Business Enterprise",
                desc: "Dell Latitude, HP EliteBook, and Lenovo ThinkPad specialized repairs.",
                icon: Building2,
                color: "bg-slate-50"
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`p-10 ${item.color} rounded-[3rem] border border-slate-100 hover:shadow-2xl transition-all group hover:bg-white`}
              >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm group-hover:bg-slate-900 transition-colors">
                  <item.icon className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">{item.brand}</h3>
                <p className="text-slate-600 font-medium leading-relaxed text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Technical Specification Table */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="bg-slate-900 rounded-[4rem] overflow-hidden shadow-2xl"
          >
            <div className="p-12 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
              <div>
                <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Service <span className="text-[#D4AF37]">Matrix</span></h2>
                <p className="text-slate-400 mt-2 font-medium">Standard engineering response times and spare part guarantees.</p>
              </div>
              <div className="flex gap-4">
                <div className="px-6 py-2 bg-white/5 rounded-full border border-white/10 text-xs text-[#D4AF37] font-bold uppercase tracking-widest">Genuine Parts Only</div>
                <div className="px-6 py-2 bg-white/5 rounded-full border border-white/10 text-xs text-[#D4AF37] font-bold uppercase tracking-widest">90 Days Warranty</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <tbody>
                  {[
                    { item: "Screen / Panel Replacement", details: "Original IPS/OLED Panels with Calibration", duration: "Same Day" },
                    { item: "Battery Restoration", details: "High-Capacity Genuine OEM Cells", duration: "2 Hours" },
                    { item: "Data Recovery", details: "Deep Scan from Dead HDDs/SSDs (Clean Room Standard)", duration: "2-3 Days" },
                    { item: "Liquid Damage Service", details: "Ultrasonic Cleaning & Trace Repair", duration: "24-48 Hours" },
                    { item: "Keyboard / Hinge Repair", details: "Original Mechanical & Membrane Assemblies", duration: "4 Hours" }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                      <td className="py-8 px-12 font-bold text-white text-lg group-hover:text-[#D4AF37] transition-colors">{row.item}</td>
                      <td className="py-8 px-12 text-slate-400 font-medium italic">{row.details}</td>
                      <td className="py-8 px-12">
                        <span className="px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-lg text-xs font-bold uppercase tracking-widest">
                          {row.duration}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>

        {/* Our Service Process Section */}
        <section className="mb-32 px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">The V4 Methodology</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our <span className="text-[#D4AF37]">Service Process</span></h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-[#D4AF37] to-[#ffd700] mx-auto mt-4 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative">
            {/* Connector Line for Desktop */}
            <div className="hidden lg:block absolute top-12 left-0 w-full h-[2px] bg-slate-100 z-0"></div>

            {[
              {
                step: "01",
                title: "In-Depth Diagnostic",
                desc: "Identifying the root cause using specialized multimeters and diagnostic software."
              },
              {
                step: "02",
                title: "Transparent Estimation",
                desc: "We provide a full cost breakdown before any work begins."
              },
              {
                step: "03",
                title: "Precision Repair",
                desc: "Execution of repair in an ESD-safe (Electrostatic Discharge) environment."
              },
              {
                step: "04",
                title: "Quality Assurance",
                desc: "Rigorous testing of all ports, sensors, and thermal levels before delivery."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative z-10 group text-center lg:text-left"
              >
                <div className="w-16 h-16 bg-white border-2 border-slate-100 text-slate-900 rounded-2xl flex items-center justify-center font-black text-2xl mb-8 group-hover:border-[#D4AF37] group-hover:text-[#D4AF37] transition-all shadow-sm mx-auto lg:mx-0">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                <p className="text-slate-600 text-sm font-medium leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Quality Standard Section */}
        <section className="mb-32">
          <div className="bg-slate-900 rounded-[4rem] p-12 sm:p-20 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <div className="space-y-4">
                  <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter uppercase leading-tight">The <span className="text-[#D4AF37]">V4 TECH</span> <br />Quality Standard</h2>
                  <h4 className="text-xl font-bold text-slate-300 tracking-tight">Why Trust Us With Your Device?</h4>
                </div>
                <p className="text-lg text-slate-400 font-medium leading-relaxed italic border-l-4 border-[#D4AF37] pl-8">
                  We follow a systematic "DevOps-style" approach to hardware servicing. Every device undergoes a strict pipeline of diagnostics, repair, and stress-testing before release.
                </p>
                <div className="pt-8">
                  <img
                    src="https://images.unsplash.com/photo-1591405351990-4726e331f141?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxsYXB0b3AlMjByZXBhaXJ8ZW58MHx8fHwxNzYzMzAxODUwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Precision Engineering"
                    className="rounded-3xl opacity-60 grayscale hover:grayscale-0 transition-all duration-700 w-full h-64 object-cover"
                  />
                </div>
              </motion.div>

              <div className="space-y-4">
                {[
                  { feature: "Component Quality", commitment: "100% Genuine OEM Spare Parts with Warranty", icon: Award },
                  { feature: "Diagnostics", commitment: "Comprehensive Multi-Point Hardware Health Check", icon: Search },
                  { feature: "Stress Testing", commitment: "Post-repair stability testing using Prime95 / FurMark", icon: Zap },
                  { feature: "Data Privacy", commitment: "Strict 'No-Access' policy to ensure your personal data remains private", icon: ShieldCheck }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-8 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-all group"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center shrink-0">
                        <item.icon className="text-[#D4AF37]" size={24} />
                      </div>
                      <div>
                        <p className="text-[#D4AF37] font-black text-xs uppercase tracking-widest mb-1">{item.feature}</p>
                        <p className="text-white font-bold text-lg leading-tight group-hover:text-[#D4AF37] transition-colors">{item.commitment}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative bg-slate-950 rounded-[4rem] p-16 sm:p-24 text-center overflow-hidden shadow-2xl group border border-white/5"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="absolute top-0 right-0 p-12 opacity-[0.05]">
            <Cpu size={300} className="text-[#D4AF37]" />
          </div>
          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl sm:text-6xl font-black text-white tracking-tighter uppercase leading-tight">
              Is Your Laptop Slow or <br /><span className="text-[#D4AF37]">Not Powering On?</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
              Get a professional diagnosis from our hardware engineers today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-6">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-16 px-12 rounded-2xl text-xl font-bold transition-all shadow-[0_0_40px_rgba(212,175,55,0.3)] flex items-center gap-3 group"
              >
                Contact Support Now
                <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('https://wa.me/919025338807', '_blank')}
                className="border-white/20 text-white hover:bg-white/10 h-16 px-12 rounded-2xl text-xl font-bold transition-all"
              >
                Direct WhatsApp
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const NetworkingSolution = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-testid="networking-page">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none bg-slate-900" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${networkingimage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/70 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Dynamic Hero Section */}
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-[#D4AF37] rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-4 shadow-xl">
              <Globe size={14} className="animate-spin-slow" /> Enterprise-Grade Connectivity
            </div>
            <h1 className="text-6xl sm:text-7xl font-bold text-white tracking-tighter leading-tight">
              High-Speed Connectivity for <br /><span className="text-[#D4AF37]">Seamless Operations.</span>
            </h1>
            <p className="text-xl text-slate-500 max-w-4xl mx-auto font-medium leading-relaxed italic">
              Enterprise-Grade Networking, Wi-Fi Optimization, and Structured Cabling Solutions for Homes and Offices.
            </p>
            <div className="flex justify-center gap-6 pt-4">
              <Button size="lg" onClick={() => navigate('/contact')} className="bg-slate-900 hover:bg-slate-800 text-white px-10 h-14 rounded-2xl font-bold shadow-xl transition-all">
                Request Site Audit
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.open('https://wa.me/919025338807', '_blank')} className="border-slate-200 text-slate-900 hover:bg-slate-50 px-10 h-14 rounded-2xl font-bold transition-all">
                Call Expert
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Technical Expertise Section */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our <span className="text-[#D4AF37]">Technical Expertise</span></h2>
                <h4 className="text-2xl font-bold text-slate-800 tracking-tight">Building the Backbone of Your Digital Environment</h4>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                A robust network is more than just a router. At V4 TECH, we architect high-performance infrastructures that ensure zero-latency communication and maximum data security.
              </p>
              <div className="pt-4 flex flex-wrap gap-8">
                <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] flex-1 min-w-[200px]">
                  <p className="text-4xl font-black text-[#D4AF37]">99.9%</p>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Uptime Standard</p>
                </div>
                <div className="bg-slate-50 border border-slate-100 p-8 rounded-[2.5rem] flex-1 min-w-[200px]">
                  <p className="text-4xl font-black text-slate-900">Zero</p>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Latency Design</p>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 gap-6">
              {[
                {
                  title: "Structured Cabling Architecture",
                  desc: "Professional deployment of CAT6/CAT6A and Fiber Optic cabling with industry-standard conduit routing and precision labeling.",
                  icon: Layout
                },
                {
                  title: "Mesh Wi-Fi & Signal Mapping",
                  desc: "Elimination of 'Dead Zones' using advanced Mesh Technology and signal strength heat-mapping for 100% wireless coverage.",
                  icon: Zap
                },
                {
                  title: "Network Segmentation (VLANs)",
                  desc: "Technical configuration of Virtual LANs to separate guest traffic from secure business data, enhancing overall network security.",
                  icon: ShieldCheck
                },
                {
                  title: "Hardware Provisioning",
                  desc: "Expert installation and configuration of Managed/Unmanaged Switches, Access Points (AP), and Load Balancers.",
                  icon: Cpu
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-8 bg-white border border-slate-100 rounded-[2.5rem] hover:border-[#D4AF3750] hover:shadow-2xl transition-all group"
                >
                  <div className="w-14 h-14 bg-slate-50 shadow-sm flex items-center justify-center shrink-0 rounded-2xl group-hover:bg-slate-900 transition-colors">
                    <item.icon className="text-[#D4AF37]" size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#D4AF37] transition-colors">{item.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Enterprise Solutions Section */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Enterprise Growth</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Enterprise <span className="text-[#D4AF37]">Solutions</span></h2>
            <h4 className="text-xl font-bold text-slate-800 tracking-tight mt-4">Scalable Infrastructure for Growing Businesses</h4>
            <p className="mt-8 text-lg text-slate-600 font-medium max-w-4xl mx-auto">
              We provide end-to-end networking services designed to handle high-traffic environments and complex data requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Office & Corporate Networking",
                desc: "Optimized setups for multi-user environments ensuring high-speed access to local servers, cloud applications, and shared peripherals (Printers/Scanners).",
                feature: "Rack Mounting & Professional Server Room Cable Management.",
                icon: Building2
              },
              {
                title: "Secure VPN & Remote Access",
                desc: "Enabling your team to work securely from anywhere with Encrypted VPN Tunnels and secure remote desktop configurations.",
                feature: "Bank-Grade Encryption.",
                icon: ShieldCheck
              },
              {
                title: "Network Security & Firewall",
                desc: "Implementation of hardware-based firewalls and MAC Address Filtering to prevent unauthorized access to your private network.",
                feature: "Threat Intelligence Integration.",
                icon: ShieldAlert
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-10 bg-white border border-slate-100 rounded-[3rem] hover:shadow-2xl transition-all group"
              >
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-8 shadow-sm group-hover:bg-slate-900 transition-colors">
                  <item.icon className="text-[#D4AF37] group-hover:text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">{item.title}</h3>
                <p className="text-slate-600 mb-6 text-sm leading-relaxed font-medium">{item.desc}</p>
                <div className="pt-6 border-t border-slate-50">
                  <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-2">Key Feature</p>
                  <p className="text-sm font-bold text-slate-800">{item.feature}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Performance Standards Section */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="bg-slate-900 rounded-[4rem] overflow-hidden shadow-2xl border border-white/5"
          >
            <div className="p-12 border-b border-white/10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div>
                <h2 className="text-3xl font-black text-[#D4AF37] uppercase tracking-tighter">Performance <span className="text-white">Standards</span></h2>
                <h4 className="text-white font-bold tracking-tight mt-2 italic">Reliability by Design</h4>
                <p className="text-slate-400 mt-4 text-sm max-w-xl font-medium">We follow a "Zero-Failure" philosophy when designing your network topology.</p>
              </div>
              <div className="flex gap-4">
                <div className="px-6 py-2 bg-white/5 rounded-full border border-white/10 text-[10px] text-[#D4AF37] font-bold uppercase tracking-widest">STP/UTP Certified</div>
                <div className="px-6 py-2 bg-white/5 rounded-full border border-white/10 text-[10px] text-[#D4AF37] font-bold uppercase tracking-widest">Multi-WAN Ready</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <tbody>
                  {[
                    { feature: "Cable Grade", standard: "Shielded Twisted Pair (STP) / Unshielded Twisted Pair (UTP) CAT6" },
                    { feature: "Speed Optimization", standard: "Gigabit Ethernet (10/100/1000 Mbps) Ready Infrastructure" },
                    { feature: "Redundancy", standard: "Dual-WAN configurations for automatic failover to backup internet" },
                    { feature: "Maintenance", standard: "Remote Network Monitoring & Periodic Firmware Updates" }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-all group">
                      <td className="py-8 px-12 font-bold text-white text-lg group-hover:text-[#D4AF37] transition-colors">{row.feature}</td>
                      <td className="py-8 px-12 text-slate-400 font-medium italic">{row.standard}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </section>

        {/* Why V4 TECH Networking Section */}
        <div className="mb-32">
          <div className="bg-slate-50 rounded-[4rem] p-12 sm:p-20 relative overflow-hidden border border-slate-200 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Why <span className="text-[#D4AF37]">V4 TECH Networking?</span></h2>
                <div className="space-y-8">
                  {[
                    { title: "Future-Proof Design", desc: "We install overhead capacity so your network can handle more devices as your business grows." },
                    { title: "Interference Mitigation", desc: "Strategic placement of Access Points to avoid RF (Radio Frequency) interference from other electronics." },
                    { title: "Neat & Organized", desc: "No messy wires. We pride ourselves on clean rack dressing and hidden cable management." },
                    { title: "Low Latency", desc: "Optimized routing tables to ensure your VoIP calls and Video Conferences never lag." }
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-6 items-start group">
                      <div className="w-12 h-12 rounded-xl bg-white border border-[#D4AF37] flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37] group-hover:text-white transition-all shadow-sm">
                        <CheckCircle size={24} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-slate-900 group-hover:text-[#D4AF37] transition-colors">{item.title}</h4>
                        <p className="text-slate-500 text-sm font-medium leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative group">
                <div className="absolute -inset-4 bg-slate-200 rounded-[3rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <img
                  src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwc2VydmVyJTIwcmFja3xlbnwwfHx8fDE3NjMzMDE4NTB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Network Infrastructure"
                  className="relative rounded-[3rem] shadow-2xl border border-slate-100 transform group-hover:scale-[1.02] transition-transform duration-500 grayscale group-hover:grayscale-0 transition-all opacity-80 group-hover:opacity-100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Our Networking Stack Section */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">Hardware Ecosystem</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase px-4">Our <span className="text-[#D4AF37]">Networking Stack</span></h2>
            <p className="mt-4 text-slate-500 font-medium">We work with industry-leading hardware providers:</p>
            <div className="h-1 w-20 bg-[#D4AF37] mx-auto mt-6 rounded-full"></div>
          </motion.div>

          <div className="flex flex-wrap justify-center items-center gap-12 sm:gap-16 lg:gap-24 px-8 opacity-60 hover:opacity-100 transition-opacity duration-700">
            {[
              "TP-Link Omada",
              "Ubiquiti UniFi",
              "Cisco",
              "D-Link",
              "Mikrotik"
            ].map((brand, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tighter hover:text-[#D4AF37] transition-all cursor-default transform hover:translate-y-[-5px]"
              >
                {brand}
              </motion.div>
            ))}
          </div>
        </section>

        {/* Global Footer CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative bg-slate-950 rounded-[4rem] p-16 sm:p-24 text-center overflow-hidden shadow-2xl group border border-white/5"
        >
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,#D4AF3715_0%,transparent_70%)]"></div>

          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl sm:text-6xl font-black text-white tracking-tighter uppercase leading-tight">
              Tired of Slow Internet or <br /><span className="text-[#D4AF37]">Wi-Fi Dead Zones?</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
              Let our network engineers optimize your connectivity for peak performance.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-6">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-16 px-12 rounded-2xl text-xl font-bold transition-all shadow-[0_0_50px_rgba(212,175,55,0.4)] flex items-center gap-3 group"
              >
                Contact Our Network Team
                <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('https://wa.me/919025338807', '_blank')}
                className="border-white/20 text-white hover:bg-white/10 h-16 px-12 rounded-2xl text-xl font-bold transition-all"
              >
                Direct WhatsApp
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const SystemCustomization = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-testid="pc-build-page">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[800px] pointer-events-none bg-slate-950" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${systemimage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/80 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '64px 64px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Dynamic Hero Section */}
        <div className="text-center mb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            <div className="inline-flex items-center gap-2 px-6 py-2 bg-white/5 backdrop-blur-xl border border-white/10 text-[#D4AF37] rounded-full text-xs font-black uppercase tracking-[0.3em] mb-4 shadow-2xl">
              <Zap size={14} className="animate-pulse" /> Extreme Performance Engineering
            </div>
            <h1 className="text-6xl sm:text-8xl font-black text-slate-100 tracking-tighter leading-tight">
              Built for <span className="text-[#D4AF37]">Power.</span> <br />
              Engineered for <span className="text-[#D4AF37]">Precision.</span>
            </h1>
            <p className="text-xl sm:text-2xl text-slate-400 max-w-4xl mx-auto font-medium leading-relaxed italic">
              Custom-Built PCs and Workstations Tailored for Gaming, Professional Editing, and Enterprise Workflows.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-8">
              <Button size="lg" onClick={() => navigate('/contact')} className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black px-12 h-16 rounded-[2rem] font-bold text-xl shadow-[0_0_50px_rgba(212,175,55,0.3)] transition-all flex items-center gap-3 group">
                Start Your Custom Build
                <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.open('https://wa.me/919025338807', '_blank')} className="border-white/20 text-white hover:bg-white/5 px-12 h-16 rounded-[2rem] font-bold text-xl transition-all">
                Consult Architect
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Section: The Art of Custom PC Building */}
        <section className="mb-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-10"
            >
              <div className="space-y-4">
                <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-tight">Performance Beyond <span className="text-[#D4AF37]">Off-the-Shelf</span> Computers</h2>
                <p className="text-lg text-slate-400 font-medium leading-relaxed italic opacity-80">
                  At V4 TECH, we don't believe in "one size fits all." Every component in our custom builds is handpicked to ensure maximum synergy, stability, and thermal efficiency.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {[
                  { title: "Workflow-Specific Architecture", desc: "Select the right CPU/GPU balance for High-FPS Gaming, 4K Video Rendering, or 3D AutoCAD Modeling." },
                  { title: "Aesthetic Excellence", desc: "Precision cable management and synchronized ARGB Lighting ecosystems for a professional setup." },
                  { title: "Thermal Engineering", desc: "AIO Liquid Cooling or premium Air Cooling installations with optimized airflow designs." },
                  { title: "Component Synergy", desc: "Perfectly matched RAM speeds, VRMs, and PSU wattage to prevent any bottlenecks." }
                ].map((item, idx) => (
                  <div key={idx} className="space-y-3 group">
                    <div className="w-10 h-1 bg-[#D4AF37] group-hover:w-20 transition-all duration-500 rounded-full"></div>
                    <h4 className="text-slate-900 font-bold text-lg group-hover:text-[#D4AF37] transition-colors">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed font-medium">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <div className="relative group">
              <div className="absolute -inset-10 bg-[#D4AF3710] rounded-full blur-[100px] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <img
                src={pcbg}
                alt="Extreme Gaming PC"
                className="relative rounded-[4rem] border border-white/10 shadow-3xl shadow-black h-[600px] w-full object-cover transform group-hover:rotate-1 group-hover:scale-[1.02] transition-all duration-700"
              />
            </div>
          </div>
        </section>

        {/* Custom Build Categories */}
        <section className="mb-40">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-[#D4AF37] font-bold tracking-[0.3em] uppercase text-xs mb-4 block underline-offset-8 underline decoration-[#D4AF3750]">Selection Guide</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Our Custom <span className="text-[#D4AF37]">Build Categories</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                title: "Professional Workstations",
                desc: "Built for architects, engineers, and content creators. High-stability for long render times.",
                opt: "Adobe Suite, Blender, SolidWorks",
                features: ["ECC Memory support", "NVMe Gen5 RAID", "Multi-Monitor Ready"],
                icon: Building2
              },
              {
                title: "Ultimate Gaming Rigs",
                desc: "Experience gaming at 1440p or 4K with ultra-high refresh rates and zero lag.",
                opt: "AAA Titles, Live Streaming, E-Sports",
                features: ["BIOS Overlock Tuning", "Low-Latency Networking", "RTX/RX Series Support"],
                icon: Zap
              },
              {
                title: "Budget-Friendly Productivity",
                desc: "Efficient and reliable systems for students, home offices, and small businesses.",
                opt: "Web Dev, Office Suites, Multitasking",
                features: ["Silent Operation", "Compact ITX/mATX", "Energy-Efficient PSU"],
                icon: Laptop
              }
            ].map((cat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-10 bg-slate-50 border border-slate-100 rounded-[3.5rem] hover:bg-white hover:text-slate-900 transition-all duration-500 overflow-hidden relative"
              >
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-8 shadow-2xl group-hover:bg-[#D4AF37] transition-all">
                    <cat.icon className="text-[#D4AF37] group-hover:text-black" size={32} />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 group-hover:text-black mb-4 h-16 flex items-center">{cat.title}</h3>
                  <p className="text-slate-500 mb-8 text-sm group-hover:text-slate-600 font-medium leading-relaxed">{cat.desc}</p>
                  <div className="space-y-4 pt-6 border-t border-white/10 group-hover:border-slate-100">
                    <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">Optimized For:</p>
                    <p className="text-xs font-bold text-slate-300 group-hover:text-slate-800 italic">{cat.opt}</p>
                    <div className="flex flex-wrap gap-2 pt-4">
                      {cat.features.map((f, fIdx) => (
                        <span key={fIdx} className="text-[9px] font-black uppercase tracking-widest px-3 py-1 bg-white/5 group-hover:bg-slate-100 rounded-full text-slate-400 group-hover:text-slate-600 border border-white/5 group-hover:border-slate-200">{f}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section: Testing Protocol Table */}
        <section className="mb-40">
          <div className="bg-slate-50 rounded-[4rem] border border-slate-100 overflow-hidden shadow-3xl">
            <div className="p-16 border-b border-slate-100 text-center sm:text-left flex flex-col sm:flex-row justify-between items-center gap-10">
              <div className="space-y-4">
                <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase leading-tight">The V4 TECH <br /><span className="text-[#D4AF37]">Testing Protocol</span></h2>
                <p className="text-slate-400 font-medium italic">Stability is Our Top Priority. Every custom system undergoes a rigorous "Burn-In" test.</p>
              </div>
              <div className="bg-slate-950 p-8 rounded-[3rem] border border-white/10 text-center min-w-[250px]">
                <p className="text-5xl font-black text-[#D4AF37]">24H</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Maximum Load Testing</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <tbody>
                  {[
                    { cat: "CPU Stress Test", method: "4-Hour Prime95 / Cinebench loop to ensure thermal stability." },
                    { cat: "GPU Benchmark", method: "FurMark / 3DMark testing for frame-rate consistency and thermal threshold." },
                    { cat: "Memory Check", method: "MemTest86 to verify RAM integrity and XMP/EXPO OC stability." },
                    { cat: "BIOS Optimization", method: "Customized Fan Curves, power profile tuning, and latest Firmware updates." }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                      <td className="py-10 px-16 font-black text-slate-900 text-xl tracking-tighter group-hover:text-[#D4AF37] transition-all uppercase">{row.cat}</td>
                      <td className="py-10 px-16 text-slate-400 font-medium italic leading-relaxed">{row.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section: Why Choose V4 TECH for Custom Builds */}
        <section className="mb-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1 relative group">
              <div className="absolute inset-20 bg-[#D4AF3715] rounded-full blur-[120px]"></div>
              <div className="grid grid-cols-2 gap-6 relative z-10">
                {[
                  { title: "Future-Proof", desc: "Select core parts for 3-5 year easy upgrades." },
                  { title: "Neat Geometry", desc: "Professional internal cable routing for airflow." },
                  { title: "Genuine Warranty", desc: "Individual part warranties + our assembly support." },
                  { title: "OS Tuning", desc: "Bloatware-free Windows, driver optimized." }
                ].map((item, idx) => (
                  <div key={idx} className="p-8 bg-slate-50 border border-slate-100 rounded-[3rem] hover:bg-[#D4AF37] group transition-all duration-500">
                    <h4 className="text-slate-900 group-hover:text-black font-black text-lg mb-2 uppercase tracking-tighter transition-colors">{item.title}</h4>
                    <p className="text-slate-500 group-hover:text-black/80 font-medium text-xs leading-relaxed transition-colors">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-10">
              <h2 className="text-5xl font-black text-slate-900 tracking-tighter uppercase leading-tight">Why Choose <br /><span className="text-[#D4AF37]">V4 TECH Custom?</span></h2>
              <p className="text-lg text-slate-400 font-medium leading-relaxed italic border-l-4 border-[#D4AF37] pl-8">
                We select every motherboard, VRM, and PSU component not just for price, but for long-term endurance and peak voltage stability.
              </p>
              <div className="flex flex-col gap-6">
                <div className="flex gap-4 items-center p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <CheckCircle className="text-[#D4AF37]" size={20} />
                  <span className="text-slate-900 text-sm font-bold opacity-80">Precision Thermal Application</span>
                </div>
                <div className="flex gap-4 items-center p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <CheckCircle className="text-[#D4AF37]" size={20} />
                  <span className="text-slate-900 text-sm font-bold opacity-80">Silent Optimized Fan Curves</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Preferred Partners */}
        <section className="mb-40">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] font-black uppercase tracking-[0.2em] text-xs">Our Preferred Partners</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-12 sm:gap-24 px-10">
            {["Intel Core", "AMD Ryzen", "NVIDIA GeForce", "ASUS ROG", "MSI", "Corsair", "G.Skill"].map((brand, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-2xl sm:text-4xl font-black text-slate-200 hover:text-[#D4AF37] transition-all cursor-default uppercase italic tracking-tighter"
              >
                {brand}
              </motion.div>
            ))}
          </div>
        </section>

        {/* High-Impact Footer CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative bg-white rounded-[4rem] p-16 sm:p-24 text-center overflow-hidden shadow-[0_0_80px_rgba(212,175,55,0.2)] group"
        >
          <div className="absolute top-0 right-0 p-12 opacity-[0.05]">
            <Cpu size={300} className="text-slate-900" />
          </div>
          <div className="relative z-10 space-y-10">
            <h2 className="text-4xl sm:text-7xl font-black text-slate-900 tracking-tighter uppercase leading-tight">
              Ready to Build Your <br /><span className="text-[#D4AF37]">Dream Machine?</span>
            </h2>
            <p className="text-xl text-slate-500 max-w-3xl mx-auto font-medium transition-all group-hover:text-slate-800">
              Consult with our hardware architects to get a personalized quote based on your budget and performance requirements.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-8 pt-6">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-slate-950 hover:bg-slate-800 text-[#D4AF37] h-20 px-16 rounded-[2.5rem] text-2xl font-black transition-all shadow-2xl flex items-center gap-4 group/btn"
              >
                Consult Our PC Architects
                <ArrowRight size={24} className="group-hover/btn:translate-x-3 transition-transform" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const SalesRetail = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-testid="sales-retail-page">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none bg-slate-900" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${salesimage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/70 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Dynamic Hero Section */}
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-6 py-2 bg-slate-900 text-[#D4AF37] rounded-full text-xs font-black uppercase tracking-[0.3em] mb-4 shadow-2xl border border-white/5">
              <Zap size={14} className="animate-pulse" /> Premium Tech Sales & Retail
            </div>
            <h1 className="text-6xl sm:text-8xl font-black text-white tracking-tighter leading-[0.9] uppercase">
              Genuine Tech.<br /><span className="text-[#D4AF37]">Unbeatable Value.</span>
            </h1>
            <p className="text-xl text-slate-500 max-w-3xl mx-auto font-medium leading-relaxed italic border-l-4 border-[#D4AF37] pl-8">
              Your One-Stop Destination for Certified Laptops, Advanced CCTV Kits, and Authentic PC Components in Kumbakonam.
            </p>
            <div className="flex justify-center gap-6 pt-6">
              <Button size="lg" onClick={() => {
                const el = document.getElementById('catalog');
                el?.scrollIntoView({ behavior: 'smooth' });
              }} className="bg-slate-900 hover:bg-slate-800 text-white px-12 h-16 rounded-2xl font-black text-lg transition-all shadow-2xl group flex items-center gap-3">
                Browse Our Catalog <ChevronDown size={20} className="group-hover:translate-y-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Section 1: Why Buy From V4 TECH? */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="relative z-10 bg-slate-900 p-12 rounded-[4rem] border border-white/5 shadow-2xl overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-[0.05] group-hover:scale-110 transition-transform duration-1000">
                  <ShieldCheck size={200} className="text-white" />
                </div>
                <h2 className="text-4xl font-black text-white tracking-tighter uppercase mb-8">The Assurance of<br /><span className="text-[#D4AF37]">Authenticity</span></h2>
                <p className="text-slate-400 text-lg font-medium leading-relaxed italic mb-8">
                  In a market flooded with duplicates, V4 TECH stands for quality. We don’t just sell boxes; we sell pre-tested, certified technology backed by full manufacturer warranties.
                </p>
                <div className="space-y-4">
                  {[
                    { label: "100% Sealed Units", icon: CheckCircle },
                    { label: "Manufacturer Warranty", icon: Award },
                    { label: "Verified Serial Numbers", icon: ShieldCheck }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 text-white/90">
                      <item.icon className="text-[#D4AF37]" size={20} />
                      <span className="font-bold text-sm uppercase tracking-widest leading-none">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Authorized Retailer",
                  desc: "Direct sourcing from leading brands like Dell, HP, Hikvision, and ASUS.",
                  icon: Building2
                },
                {
                  title: "Pre-Delivery Inspection",
                  desc: "Every laptop and component undergoes a hardware check before it reaches you.",
                  icon: Search
                },
                {
                  title: "Competitive Pricing",
                  desc: "Get online-competitive prices right here in Natchiyar Kovil with local support.",
                  icon: Zap
                },
                {
                  title: "Expert Guidance",
                  desc: "Our tech experts help you choose the right specs, not the most expensive one.",
                  icon: Users
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] hover:bg-white hover:border-[#D4AF3750] hover:shadow-2xl transition-all group"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-slate-900 transition-colors">
                    <item.icon className="text-[#D4AF37]" size={28} />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tighter uppercase">{item.title}</h3>
                  <p className="text-slate-600 text-sm font-medium leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2: Our Product Categories */}
        <section id="catalog" className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-[#D4AF37] font-black tracking-[0.3em] uppercase text-xs mb-4 block">Product Catalog</span>
            <h2 className="text-4xl sm:text-6xl font-black text-slate-900 tracking-tighter uppercase leading-none">Our Product <span className="text-[#D4AF37]">Categories</span></h2>
            <div className="h-2 w-32 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] mx-auto mt-8 rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* 1. Laptops & Computing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group relative bg-white border border-slate-100 rounded-[4rem] p-12 hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[4rem] -mr-8 -mt-8 group-hover:bg-[#D4AF3710] transition-colors"></div>
              <div className="w-20 h-20 bg-slate-900 rounded-[2rem] flex items-center justify-center mb-10 shadow-2xl group-hover:bg-[#D4AF37] transition-colors">
                <Laptop className="text-white group-hover:text-slate-900 transition-colors" size={36} />
              </div>
              <h3 className="text-3xl font-black text-slate-900 mb-8 tracking-tighter uppercase group-hover:text-[#D4AF37] transition-colors">1. Laptops & Computing</h3>
              <p className="text-slate-600 mb-8 font-medium text-sm leading-relaxed italic">From student-friendly notebooks to high-end professional workstations.</p>
              <ul className="space-y-6">
                {[
                  { label: "New Arrivals", desc: "14th Gen Intel and Ryzen 8000 series." },
                  { label: "Business Series", desc: "Durable Dell Latitude & ThinkPads." },
                  { label: "Accessories", desc: "Original batteries, mice, and keyboards." }
                ].map((item, i) => (
                  <li key={i} className="flex flex-col gap-1 border-l-2 border-slate-100 pl-6 group-hover:border-[#D4AF37] transition-colors">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                    <span className="text-sm font-bold text-slate-900 leading-tight">{item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 2. Security & Surveillance Kits */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative bg-slate-950 border border-white/5 rounded-[4rem] p-12 shadow-2xl hover:shadow-[#D4AF3720] transition-all duration-500 overflow-hidden text-white"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF3710] rounded-bl-[4rem] -mr-8 -mt-8"></div>
              <div className="w-20 h-20 bg-[#D4AF37] rounded-[2rem] flex items-center justify-center mb-10 shadow-2xl">
                <Shield className="text-slate-950" size={36} />
              </div>
              <h3 className="text-3xl font-black text-white mb-8 tracking-tighter uppercase group-hover:text-[#D4AF37] transition-colors">2. Security & Surveillance</h3>
              <p className="text-slate-400 mb-8 font-medium text-sm leading-relaxed italic">Complete DIY kits or professionally curated security bundles.</p>
              <ul className="space-y-6">
                {[
                  { label: "CCTV Bundles", desc: "4/8-Channel HD camera kits with DVR." },
                  { label: "Smart Security", desc: "Wi-Fi PTZ cams and smart video locks." },
                  { label: "Storage Solutions", desc: "WD Purple & Seagate Surveillance HDDs." }
                ].map((item, i) => (
                  <li key={i} className="flex flex-col gap-1 border-l-2 border-white/10 pl-6 group-hover:border-[#D4AF37] transition-colors">
                    <span className="text-xs font-black text-[#D4AF37] uppercase tracking-widest">{item.label}</span>
                    <span className="text-sm font-bold text-white leading-tight">{item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 3. PC Components & Peripherals */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative bg-white border border-slate-100 rounded-[4rem] p-12 hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[4rem] -mr-8 -mt-8 group-hover:bg-[#D4AF3710] transition-colors"></div>
              <div className="w-20 h-20 bg-slate-900 rounded-[2rem] flex items-center justify-center mb-10 shadow-2xl group-hover:bg-[#D4AF37] transition-colors">
                <Cpu className="text-white group-hover:text-slate-900 transition-colors" size={36} />
              </div>
              <h3 className="text-3xl font-black text-slate-900 mb-8 tracking-tighter uppercase group-hover:text-[#D4AF37] transition-colors">3. PC Components</h3>
              <p className="text-slate-600 mb-8 font-medium text-sm leading-relaxed italic">Everything you need to build or upgrade your desktop.</p>
              <ul className="space-y-6">
                {[
                  { label: "Core Parts", desc: "CPUs, High-Speed RAM, and GPUs." },
                  { label: "Storage Upgrades", desc: "NVMe M.2 SSDs and External Drives." },
                  { label: "Networking Gear", desc: "High-gain Routers and Mesh Wi-Fi." }
                ].map((item, i) => (
                  <li key={i} className="flex flex-col gap-1 border-l-2 border-slate-100 pl-6 group-hover:border-[#D4AF37] transition-colors">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                    <span className="text-sm font-bold text-slate-900 leading-tight">{item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Section 3: Retail Comparison & Value */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] font-black tracking-[0.3em] uppercase text-xs mb-4 block">The V4 Advantage</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase leading-none text-center">Retail <span className="text-[#D4AF37]">Comparison</span></h2>
            <div className="h-1.5 w-24 bg-[#D4AF37] mx-auto mt-8 rounded-full"></div>
          </motion.div>

          <div className="bg-slate-950 rounded-[4rem] overflow-hidden shadow-2xl border border-white/5">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-900 border-b border-white/5">
                    <th className="p-10 text-xs font-black text-[#D4AF37] uppercase tracking-[0.3em]">Feature</th>
                    <th className="p-10 text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Buying Online</th>
                    <th className="p-10 text-xs font-black text-white uppercase tracking-[0.3em] bg-white/5">Buying at V4 TECH</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { f: "Warranty Support", online: "Ship it back to the brand", v4: "Direct walk-in support at our store" },
                    { f: "Setup & OS", online: "DIY (Do it yourself)", v4: "Free OS optimization & driver setup" },
                    { f: "Genuineness", online: "Risk of refurbished units", v4: "100% Brand New & Sealed Units" },
                    { f: "Expert Advice", online: "Based on generic reviews", v4: "Personalized based on your need" }
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-white/5 transition-colors group">
                      <td className="p-10 text-lg font-black text-white uppercase tracking-tighter group-hover:text-[#D4AF37] transition-colors">{row.f}</td>
                      <td className="p-10 text-slate-400 font-medium italic">{row.online}</td>
                      <td className="p-10 text-white font-bold bg-white/5 italic underline decoration-[#D4AF37] underline-offset-8">{row.v4}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 4: Bulk & Corporate Orders */}
        <section className="mb-32">
          <div className="bg-slate-50 border border-slate-200 rounded-[5rem] p-16 sm:p-24 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-12 opacity-[0.03] group-hover:rotate-12 transition-transform duration-1000">
              <Building2 size={400} className="text-slate-900" />
            </div>
            <div className="max-w-4xl relative z-10">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-[#D4AF37] font-black tracking-[0.3em] uppercase text-xs mb-4 block">B2B & Enterprise</span>
                <h2 className="text-4xl sm:text-7xl font-black text-slate-900 tracking-tighter uppercase leading-[0.9] mb-8">
                  Equip Your Office or <span className="text-[#D4AF37]">Institution</span>
                </h2>
                <p className="text-xl text-slate-600 font-medium leading-relaxed italic mb-12 max-w-3xl border-l-4 border-slate-900 pl-8">
                  Planning to upgrade your office or school lab? we offer specialized B2B Pricing for bulk orders, ensuring you get the best tech at the most efficient price points.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                  {[
                    "Custom Quotations for 5+ units",
                    "On-site delivery and installation",
                    "Priority Technical Support",
                    "Annual Maintenance Contracts (AMC)"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 group/item">
                      <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center shrink-0 group-hover/item:bg-[#D4AF37] transition-colors">
                        <CheckCircle className="text-white" size={16} />
                      </div>
                      <span className="text-lg font-bold text-slate-900 tracking-tight">{item}</span>
                    </div>
                  ))}
                </div>
                <Button size="lg" onClick={() => navigate('/contact')} className="bg-[#D4AF37] hover:bg-slate-900 text-white px-12 h-16 rounded-2xl font-black text-xl transition-all shadow-2xl flex items-center gap-4 group/btn">
                  Request B2B Quote <ArrowRight size={24} className="group-hover/btn:translate-x-3 transition-transform" />
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Section 5: Our Featured Brands */}
        <section className="mb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl font-black text-slate-400 uppercase tracking-[0.5em] mb-12">Authorized Brands</h2>
            <div className="flex flex-wrap justify-center gap-12 lg:gap-20 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-1000">
              {["HP", "DELL", "LENOVO", "ASUS", "HIKVISION", "DAHUA", "WD", "LOGITECH"].map((brand) => (
                <span key={brand} className="text-4xl font-black text-slate-900 italic tracking-[0.2em]">{brand}</span>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-slate-950 rounded-[4rem] p-16 sm:p-24 text-center overflow-hidden shadow-2xl group border border-white/5"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="relative z-10 space-y-10">
            <h2 className="text-4xl sm:text-7xl font-black text-white tracking-tighter uppercase leading-[0.9]">
              Looking for a Specific<br /><span className="text-[#D4AF37]">Model or Part?</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto font-medium italic transition-all group-hover:text-white">
              Check our current stock or pre-order the latest tech gadgets today. Our procurement tea is ready to source any component you need.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-8 pt-8">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-[#D4AF37] hover:bg-white text-black h-20 px-16 rounded-[2.5rem] text-2xl font-black transition-all shadow-[0_0_50px_rgba(212,175,55,0.4)] flex items-center gap-4 group/btn"
              >
                Enquire About Product
                <ArrowRight size={24} className="group-hover/btn:translate-x-3 transition-transform" />
              </Button>
            </div>
          </div>
          {/* Animated Background Icon */}
          <div className="absolute -left-20 -bottom-20 opacity-[0.03] text-[#D4AF37] -rotate-12 pointer-events-none group-hover:opacity-[0.06] transition-opacity duration-1000">
            <Search size={500} strokeWidth={0.5} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8" data-testid="contact-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Contact Us</h1>
          <p className="text-lg text-slate-700">Get in touch with our team</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <Card className="bg-slate-50 border border-slate-200 rounded-xl shadow-2xl mb-8">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="send-message-btn">
                    {submitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#D4AF3720] border border-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-slate-900" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Phone</h3>
                  <p className="text-slate-600">+91 90253 38807</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#D4AF3720] border border-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-slate-900" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Email</h3>
                  <p className="text-slate-600">vasanv4tech@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#D4AF3720] border border-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-slate-900" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Address</h3>
                  <p className="text-slate-600">  Neay by Thearady ,Kilaveethi ,Natchiyar Kovil<br />Kumbakonam, Tamil Nadu 612602</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <Card className="border-0 shadow-2xl overflow-hidden h-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d15670.619502015286!2d79.4424576532009!3d10.913811957807836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTDCsDU0JzU4LjciTiA3OcKwMjYnNTAuNSJF!5e0!3m2!1sen!2sin!4v1763662213049!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "500px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="V4 Tech Location"
              ></iframe>
            </Card>
          </div>
        </div>

        <div className="text-center mt-12">
          <Button
            size="lg"
            onClick={() => window.open('https://wa.me/919025338807', '_blank')}
            className="bg-green-600 hover:bg-green-700"
            data-testid="whatsapp-btn"
          >
            Chat on WhatsApp
          </Button>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [customerDetails, setCustomerDetails] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [publicStats, setPublicStats] = useState({ customers: "", projects: "", team: "" });
  const [updatingStats, setUpdatingStats] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    }
  }, [location]);

  const processSession = async (sessionId) => {
    try {
      await axios.post(`${API}/auth/session`, null, {
        params: { session_id: sessionId },
        withCredentials: true
      });
      window.location.hash = '';
      window.location.reload();
    } catch (error) {
      toast.error("Authentication failed");
      navigate('/');
    }
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchAllData();
    } catch (error) {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [statsRes, detailsRes, reviewsRes, complaintsRes, messagesRes, publicStatsRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/customer-details`, { withCredentials: true }),
        axios.get(`${API}/reviews?approved_only=false`, { withCredentials: true }),
        axios.get(`${API}/complaints`, { withCredentials: true }),
        axios.get(`${API}/contact`, { withCredentials: true }),
        axios.get(`${API}/public-stats`)
      ]);

      setStats(statsRes.data);
      setCustomerDetails(detailsRes.data);
      setReviews(reviewsRes.data);
      setComplaints(complaintsRes.data);
      setMessages(messagesRes.data);
      setPublicStats(publicStatsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const handleUpdatePublicStats = async (e) => {
    e.preventDefault();
    setUpdatingStats(true);
    try {
      await axios.patch(`${API}/public-stats`, publicStats, { withCredentials: true });
      toast.success("Website statistics updated successfully!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update statistics");
    } finally {
      setUpdatingStats(false);
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}/approve`, null, { withCredentials: true });
      toast.success("Review approved");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to approve review");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const handleUpdateComplaintStatus = async (complaintId, status) => {
    try {
      await axios.patch(`${API}/complaints/${complaintId}/status`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleUpdateCustomerStatus = async (detailId, status) => {
    try {
      await axios.patch(`${API}/customer-details/${detailId}/status`, null, {
        params: { status },
        withCredentials: true
      });
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteCustomer = async (detailId) => {
    if (!window.confirm("Are you sure you want to delete this customer request?")) return;

    try {
      await axios.delete(`${API}/customer-details/${detailId}`, { withCredentials: true });
      toast.success("Customer request deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete customer request");
    }
  };

  const handleUpdateCustomer = async (detailId, data) => {
    try {
      await axios.patch(`${API}/customer-details/${detailId}`, data, { withCredentials: true });
      toast.success("Customer details updated");
      setEditingCustomer(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update customer details");
    }
  };

  const handleDeleteComplaint = async (complaintId) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) return;

    try {
      await axios.delete(`${API}/complaints/${complaintId}`, { withCredentials: true });
      toast.success("Complaint deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete complaint");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    try {
      await axios.delete(`${API}/contact/${messageId}`, { withCredentials: true });
      toast.success("Message deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleUpdateReview = async (reviewId, data) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}`, data, { withCredentials: true });
      toast.success("Review updated");
      setEditingReview(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user?.name}</p>
        </div>

        <div className="flex gap-4 mb-8 overflow-x-auto">
          <Button variant={activeTab === "dashboard" ? "default" : "outline"} onClick={() => setActiveTab("dashboard")} data-testid="dashboard-tab">Dashboard</Button>
          <Button variant={activeTab === "customers" ? "default" : "outline"} onClick={() => setActiveTab("customers")} data-testid="customers-tab">Customer Requests</Button>
          <Button variant={activeTab === "reviews" ? "default" : "outline"} onClick={() => setActiveTab("reviews")} data-testid="reviews-tab">Reviews</Button>
          <Button variant={activeTab === "complaints" ? "default" : "outline"} onClick={() => setActiveTab("complaints")} data-testid="complaints-tab">Complaints</Button>
          <Button variant={activeTab === "messages" ? "default" : "outline"} onClick={() => setActiveTab("messages")} data-testid="messages-tab">Messages</Button>
          <Button variant={activeTab === "webstats" ? "default" : "outline"} onClick={() => setActiveTab("webstats")} data-testid="webstats-tab">Website Stats</Button>
        </div>

        {activeTab === "dashboard" && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-section">
            <Card className="bg-slate-50 border border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-sm text-slate-900">Total Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl text-[#D4AF37] font-bold">{stats.total_customers}</p>
                <p className="text-sm text-slate-600 mt-1">{stats.pending_customers} pending</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 border border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-sm text-slate-900">Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl text-[#D4AF37] font-bold">{stats.total_reviews}</p>
                <p className="text-sm text-slate-600 mt-1">{stats.pending_reviews} pending approval</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 border border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-sm text-slate-900">Complaints</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl text-[#D4AF37] font-bold">{stats.total_complaints}</p>
                <p className="text-sm text-slate-600 mt-1">{stats.pending_complaints} pending</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 border border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-sm text-slate-900">Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl text-[#D4AF37] font-bold">{stats.total_messages}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "customers" && (
          <div className="space-y-4" data-testid="customers-section">
            {customerDetails.map((detail) => (
              <Card key={detail.id} className="bg-slate-50 border border-slate-200 shadow-lg">
                <CardContent className="p-6">
                  {editingCustomer?.id === detail.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={editingCustomer.name}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={editingCustomer.email}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={editingCustomer.phone}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Service Needed</Label>
                          <Input
                            value={editingCustomer.service_needed}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, service_needed: e.target.value })}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Address</Label>
                          <Textarea
                            value={editingCustomer.address}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Description</Label>
                          <Textarea
                            value={editingCustomer.description}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, description: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleUpdateCustomer(detail.id, editingCustomer)}>Save</Button>
                        <Button variant="outline" onClick={() => setEditingCustomer(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-900">Name</p>
                          <p className="font-semibold">{detail.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-900">Email</p>
                          <p>{detail.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-900">Phone</p>
                          <p>{detail.phone}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-900">Service</p>
                          <p>{detail.service_needed}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm text-slate-900">Address</p>
                          <p>{detail.address}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm text-slate-900">Description</p>
                          <p>{detail.description}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-900 mb-2">Status</p>
                          <Select value={detail.status} onValueChange={(value) => handleUpdateCustomerStatus(detail.id, value)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className='bg-slate-50 border border-slate-200 shadow-lg'>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => setEditingCustomer(detail)} data-testid={`edit-customer-${detail.id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteCustomer(detail.id)} data-testid={`delete-customer-${detail.id}`}>
                          Delete
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            {customerDetails.length === 0 && (
              <p className="text-center text-slate-900 py-8">No customer requests yet</p>
            )}
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="space-y-4" data-testid="reviews-section">
            {reviews.map((review) => (
              <Card key={review.id} className="bg-slate-50 border border-slate-200 shadow-lg">
                <CardContent className="p-6">
                  {editingReview?.id === review.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Customer Name</Label>
                          <Input
                            value={editingReview.customer_name}
                            onChange={(e) => setEditingReview({ ...editingReview, customer_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={editingReview.email}
                            onChange={(e) => setEditingReview({ ...editingReview, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Service Taken</Label>
                          <Input
                            value={editingReview.service_taken}
                            onChange={(e) => setEditingReview({ ...editingReview, service_taken: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Rating</Label>
                          <div className="flex gap-2 mt-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setEditingReview({ ...editingReview, rating: star })}
                              >
                                <Star
                                  size={24}
                                  fill={star <= editingReview.rating ? "#FFA500" : "none"}
                                  color={star <= editingReview.rating ? "#FFA500" : "#gray"}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <Label>Review Text</Label>
                          <Textarea
                            value={editingReview.review_text}
                            onChange={(e) => setEditingReview({ ...editingReview, review_text: e.target.value })}
                            rows={4}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleUpdateReview(review.id, editingReview)}>Save</Button>
                        <Button variant="outline" onClick={() => setEditingReview(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-semibold text-lg">{review.customer_name}</p>
                          <p className="text-sm text-slate-900">{review.email}</p>
                          <p className="text-sm text-slate-900">{review.service_taken}</p>
                        </div>
                        <div className="flex">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} size={16} fill="#FFA500" color="#FFA500" />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-900 mb-4">{review.review_text}</p>
                      <div className="flex gap-2 flex-wrap">
                        {!review.approved && (
                          <Button onClick={() => handleApproveReview(review.id)} size="sm" data-testid={`approve-review-${review.id}`}>Approve</Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setEditingReview(review)} data-testid={`edit-review-${review.id}`}>
                          Edit
                        </Button>
                        <Button onClick={() => handleDeleteReview(review.id)} variant="destructive" size="sm" data-testid={`delete-review-${review.id}`}>Delete</Button>
                        {review.approved && <span className="text-green-600 text-sm flex items-center"><CheckCircle size={16} className="mr-1" /> Approved</span>}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            {reviews.length === 0 && (
              <p className="text-center text-slate-900 py-8">No reviews yet</p>
            )}
          </div>
        )}

        {activeTab === "complaints" && (
          <div className="space-y-4" data-testid="complaints-section">
            {complaints.map((complaint) => (
              <Card key={complaint.id} className="bg-slate-50 border border-slate-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-900">Complaint ID</p>
                      <p className="font-semibold">{complaint.complaint_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900">Customer</p>
                      <p>{complaint.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900">Email</p>
                      <p>{complaint.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900">Phone</p>
                      <p>{complaint.phone}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-slate-900">Subject</p>
                      <p className="font-semibold">{complaint.subject}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-slate-900">Description</p>
                      <p>{complaint.description}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900 mb-2">Status</p>
                      <Select value={complaint.status} onValueChange={(value) => handleUpdateComplaintStatus(complaint.complaint_id, value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='bg-slate-50 border border-slate-200 shadow-lg'>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-slate-900">Submitted</p>
                      <p>{new Date(complaint.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteComplaint(complaint.complaint_id)} data-testid={`delete-complaint-${complaint.id}`}>
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {complaints.length === 0 && (
              <p className="text-center text-slate-900 py-8">No complaints yet</p>
            )}
          </div>
        )}

        {activeTab === "messages" && (
          <div className="space-y-4" data-testid="messages-section">
            {messages.map((message) => (
              <Card key={message.id} className="bg-slate-50 border border-slate-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <p className="font-semibold text-lg">{message.name}</p>
                    <p className="text-sm text-slate-900">{message.email}</p>
                    {message.phone && <p className="text-sm text-slate-900">Phone: {message.phone}</p>}
                  </div>
                  <p className="text-slate-600">{message.message}</p>
                  <p className="text-sm text-slate-600 mt-4">{new Date(message.created_at).toLocaleString()}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteMessage(message.id)} data-testid={`delete-message-${message.id}`}>
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {messages.length === 0 && (
              <p className="text-center text-slate-900 py-8">No messages yet</p>
            )}
          </div>
        )}

        {activeTab === "webstats" && (
          <div className="max-w-2xl mx-auto" data-testid="webstats-section">
            <Card className="bg-slate-50 border border-slate-200 shadow-xl rounded-3xl overflow-hidden">
              <CardHeader className="bg-slate-900 text-white p-8">
                <CardTitle className="text-2xl font-bold flex items-center gap-3">
                  <Globe className="text-[#D4AF37]" />
                  Manage Website Statistics
                </CardTitle>
                <CardDescription className="text-slate-400">Update the numbers shown in the homepage stats section</CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleUpdatePublicStats} className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-slate-900 font-bold">Happy Customers (e.g. 500+)</Label>
                    <Input
                      value={publicStats.customers}
                      onChange={(e) => setPublicStats({ ...publicStats, customers: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      placeholder="500+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-900 font-bold">Projects Completed (e.g. 1200+)</Label>
                    <Input
                      value={publicStats.projects}
                      onChange={(e) => setPublicStats({ ...publicStats, projects: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      placeholder="1200+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-900 font-bold">Expert Team Members (e.g. 15+)</Label>
                    <Input
                      value={publicStats.team}
                      onChange={(e) => setPublicStats({ ...publicStats, team: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      placeholder="15+"
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 bg-[#D4AF37] hover:bg-[#F4D03F] text-black font-bold text-lg" disabled={updatingStats}>
                    {updatingStats ? "Updating..." : "Save Changes"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

const MobileSolution = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      {/* Full-Width Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none bg-slate-900" >
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${mobileimage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/70 to-white pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
      </div>

      {/* Hero Content Section */}
      <section className="relative py-24 mb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-[#D4AF37] text-sm font-bold tracking-widest uppercase mb-6">
            <Phone size={16} /> Mobile Solutions
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tighter">
            Precision Mobile Engineering. <span className="text-[#D4AF37]">Factory-Grade Restorations.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
            Expert Chip-Level Repairs, Display Replacements, and Advanced Data Recovery for All Major Smartphone Brands.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Button size="lg" onClick={() => navigate('/contact')} className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-14 px-8 text-lg font-bold">
              Get a Repair Quote <ArrowRight className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-32">
        {/* Section 1: Our Technical Expertise */}
        <section>
          <div className="mb-12 text-center md:text-left">
            <span className="text-[#D4AF37] font-bold tracking-widest uppercase text-sm block mb-2">Our Technical Expertise</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter">Micro-Soldering & Component-Level Excellence</h2>
            <p className="text-lg text-slate-600 mt-4 max-w-3xl">Modern smartphones are complex. At V4 TECH, we don't just swap parts; we diagnose and repair the motherboard to save you from expensive replacements.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: "Advanced Logic Board Repair", desc: "Expertise in fixing 'Dead' phones, Charging IC issues, Audio IC problems, and Short-Circuit troubleshooting.", icon: Cpu },
              { title: "Display & Touch Calibration", desc: "Premium OEM Screen replacements with high-color accuracy and perfect touch sensitivity for iPhone and Flagship Androids.", icon: Monitor },
              { title: "ESD-Safe Environment", desc: "All repairs are conducted in an Electrostatic Discharge (ESD) safe lab to protect sensitive internal components.", icon: ShieldCheck },
              { title: "Water Damage Recovery", desc: "Specialized ultrasonic cleaning and chemical treatment to revive devices after liquid exposure.", icon: Zap }
            ].map((item, idx) => (
              <motion.div key={idx} whileHover={{ y: -5 }} className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 flex gap-6 group">
                <div className="w-14 h-14 shrink-0 rounded-2xl bg-slate-50 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                  <item.icon className="text-[#D4AF37] group-hover:text-white" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 font-medium leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section 2: Specialized Solutions */}
        <section>
          <div className="mb-12 text-center">
            <span className="text-[#D4AF37] font-bold tracking-widest uppercase text-sm block mb-2">Specialized Solutions</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter">Specialized Solutions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-slate-900 text-white p-10 rounded-[3rem] border border-slate-800 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-6 flex items-center gap-3"><Award /> 1. iPhone & Premium Flagship Care</h3>
              <p className="text-slate-400 mb-6">Dedicated service for high-end devices like iPhone, Samsung S-Series, and Pixel.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><CheckCircle className="text-[#D4AF37] shrink-0 mt-1" size={20} /> <div><span className="font-bold text-white">FaceID & TouchID Repair:</span> <span className="text-slate-400">Restoring biometric functionality with precision tools.</span></div></li>
                <li className="flex items-start gap-3"><CheckCircle className="text-[#D4AF37] shrink-0 mt-1" size={20} /> <div><span className="font-bold text-white">Battery Health Calibration:</span> <span className="text-slate-400">Genuine battery replacements that show accurate health percentages and peak performance.</span></div></li>
              </ul>
            </div>

            <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-xl shadow-slate-200/50 relative overflow-hidden">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3"><Database className="text-[#D4AF37]" /> 2. Software & System Recovery</h3>
              <ul className="space-y-6 mt-8">
                <li className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 flex items-center justify-center rounded-xl shrink-0"><Laptop className="text-blue-500" /></div>
                  <div><h4 className="font-bold text-slate-900 text-lg">OS Flashing</h4><p className="text-slate-600">Fixing boot-loops, stuck logos, and corrupted system files.</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 flex items-center justify-center rounded-xl shrink-0"><HardDrive className="text-green-500" /></div>
                  <div><h4 className="font-bold text-slate-900 text-lg">Data Recovery</h4><p className="text-slate-600">Recovering your precious photos and contacts from physically damaged or non-powering phones.</p></div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3: The V4 TECH Mobile Standard */}
        <section className="bg-white p-12 rounded-[3rem] border border-[#D4AF37]/20 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2940')] bg-cover bg-center opacity-[0.03]"></div>
          <div className="relative z-10 text-center mb-12">
            <span className="text-[#D4AF37] font-bold tracking-widest uppercase text-sm block mb-2">The V4 TECH Standard</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter">The V4 TECH Mobile Standard</h2>
            <p className="text-lg text-slate-600 mt-4">Quality You Can See. Performance You Can Feel.</p>
          </div>
          <div className="relative z-10 overflow-x-auto">
            <table className="w-full text-left bg-slate-50 rounded-2xl overflow-hidden">
              <thead className="bg-slate-900 text-white">
                <tr><th className="px-6 py-4 font-bold text-lg">Service Feature</th><th className="px-6 py-4 font-bold text-lg">Our Commitment</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr className="hover:bg-slate-100 transition-colors"><td className="px-6 py-5 font-bold text-slate-900">Tooling</td><td className="px-6 py-5 text-slate-700">Professional Micro-Soldering Stations & Microscopes</td></tr>
                <tr className="hover:bg-slate-100 transition-colors"><td className="px-6 py-5 font-bold text-slate-900">Parts Quality</td><td className="px-6 py-5 text-slate-700">A+ Grade / Original Equipment Manufacturer (OEM) Spares</td></tr>
                <tr className="hover:bg-slate-100 transition-colors"><td className="px-6 py-5 font-bold text-slate-900">Turnaround Time</td><td className="px-6 py-5 text-slate-700">Same-Day Service for Screens & Batteries (Subject to Stock)</td></tr>
                <tr className="hover:bg-slate-100 transition-colors"><td className="px-6 py-5 font-bold text-slate-900">Post-Repair</td><td className="px-6 py-5 text-slate-700">48-Hour Stability Check for Complex Board Repairs</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4: Why Choose Us & Brands */}
        <section>
          <div className="mb-12 text-center">
            <span className="text-[#D4AF37] font-bold tracking-widest uppercase text-sm block mb-2">Why Choose Us</span>
            <h2 className="text-4xl font-black text-slate-900 tracking-tighter">Why Choose Us for Mobile Repairs?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            {[
              { t: "Micro-Precision", d: "We handle even the smallest 01005 sized components under microscopes.", i: Zap },
              { t: "No Data Loss", d: "We prioritize keeping your data safe during hardware repairs.", i: Shield },
              { t: "Transparent Diagnostics", d: "We show you the faulty component before we begin the repair process.", i: Search },
              { t: "Warranty Support", d: "All premium repairs come with a dedicated V4 TECH service warranty.", i: Award }
            ].map((i, id) => (
              <div key={id} className="text-center p-6 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 hover:-translate-y-2 transition-transform">
                <div className="w-16 h-16 bg-[#D4AF37]/10 text-[#D4AF37] rounded-2xl flex items-center justify-center mx-auto mb-4"><i.i size={32} /></div>
                <h4 className="font-bold text-slate-900 mb-2">{i.t}</h4>
                <p className="text-sm text-slate-600">{i.d}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Brands We Service</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['Apple (iPhone)', 'Samsung', 'OnePlus', 'Google Pixel', 'Xiaomi', 'Vivo', 'Oppo', 'RealMe'].map((brand, bId) => (
                <div key={bId} className="px-6 py-3 bg-white border border-slate-200 rounded-full text-slate-800 font-bold shadow-sm">{brand}</div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-slate-900 rounded-[3rem] p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544197150-b99a580bb7a8')] bg-cover opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Broken Screen or Phone Not Charging?</h2>
            <p className="text-xl text-slate-400 mb-10">Don't wait. Bring your device to our expert technicians for a fast and reliable fix.</p>
            <Button size="lg" onClick={() => navigate('/contact')} className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black h-16 px-10 text-lg font-bold">
              Contact Our Mobile Experts
            </Button>
          </div>
        </section>

      </div>
    </div>
  );
};

/*footer*/

const Footer = () => {
  return (
    <footer className="bg-white text-slate-700 relative overflow-hidden border-t border-slate-100">
      {/* Animated Background Decoration for Footer */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
        <div className="tech-grid-bg"></div>
      </div>

      <div className="w-full px-6 sm:px-12 lg:px-24 py-20 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Brand Section */}
          <div className="space-y-6 lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4"
            >
              <img src={Logo} alt="V4 TECH Logo" className="h-12 w-auto" />
              <h2 className="text-3xl font-black text-slate-900 tracking-tighter">V4 TECH</h2>
            </motion.div>
            <p className="text-slate-600 text-base max-w-sm leading-relaxed">
              Premium technology partner specializing in Security Systems, Computer Hardware, and Advanced Web Solutions. We build future-ready infrastructure for your home and business.
            </p>

            <div className="flex gap-4 pt-4">
              {['Facebook', 'Instagram', 'Twitter', 'LinkedIn'].map((social) => (
                <motion.a
                  key={social}
                  whileHover={{ y: -5, color: '#D4AF37' }}
                  href="#"
                  className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 transition-colors border border-slate-100 shadow-sm"
                >
                  <span className="sr-only">{social}</span>
                  <Globe size={18} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-slate-900 mb-8 border-l-2 border-[#D4AF37] pl-4">Navigation</h4>
            <ul className="space-y-4 text-slate-600 font-medium">
              {['Home', 'About', 'Reviews', 'Support', 'Contact'].map((item) => (
                <li key={item}>
                  <Link to={item === 'Home' ? '/' : `/${item.toLowerCase()}`} className="hover:text-[#D4AF37] transition-all flex items-center gap-2 group">
                    <span className="w-0 group-hover:w-4 h-0.5 bg-[#D4AF37] transition-all"></span>
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-slate-900 mb-8 border-l-2 border-[#D4AF37] pl-4">Contact Us</h4>
            <div className="space-y-6 text-slate-600">
              <div className="flex gap-4">
                <Phone size={20} className="text-[#D4AF37] shrink-0" />
                <p className="text-sm font-semibold">+91 90253 38807</p>
              </div>
              <div className="flex gap-4">
                <Mail size={20} className="text-[#D4AF37] shrink-0" />
                <p className="text-sm font-semibold break-all">vasanv4tech@gmail.com</p>
              </div>
              <div className="flex gap-4">
                <MapPin size={20} className="text-[#D4AF37] shrink-0" />
                <p className="text-xs leading-relaxed">Near by Thearady, Kilaveethi, Natchiyar Kovil, Kumbakonam, TN 612602</p>
              </div>
            </div>
          </div>

          {/* Animated Support Card / Filler */}
          <div className="lg:col-span-1">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 relative overflow-hidden group shadow-xl shadow-slate-100 border border-[#D4AF37]/30"
            >
              {/* Gold top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F4D03F] to-[#D4AF37] rounded-t-3xl"></div>

              <div className="relative z-10">
                {/* Live indicator */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs font-bold text-green-600 uppercase tracking-widest">Online Now</span>
                </div>
                <h4 className="text-xl font-black text-slate-900 mb-2">Live Support</h4>
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">Need instant help? Connect with our technical experts on WhatsApp right now.</p>
                <a href="https://wa.me/919025338807" className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-xl text-sm font-bold transition-all transform hover:translate-x-1 shadow-lg shadow-green-200">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Message Now
                </a>
              </div>
              {/* Subtle background decoration */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -right-8 -bottom-8 opacity-[0.06] group-hover:opacity-[0.1] transition-opacity"
              >
                <Globe size={140} className="text-[#D4AF37]" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Social / WhatsApp Bottom Bar */}
        <div className="mt-20 pt-10 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Systems Online & Ready</p>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
            <p className="text-sm text-slate-400 font-medium tracking-tight">© {new Date().getFullYear()} V4 TECH ENTERPRISE. All Rights Reserved.</p>
            <div className="flex gap-8">
              <a href="#" className="text-xs font-bold text-slate-900 hover:text-[#D4AF37] transition-colors uppercase tracking-widest">Privacy Policy</a>
              <a href="#" className="text-xs font-bold text-slate-900 hover:text-[#D4AF37] transition-colors uppercase tracking-widest">Terms</a>
            </div>
          </div>
        </div>
      </div>

      {/* Large Decorative Fading Logo in background */}
      <div className="absolute right-[-10%] bottom-[-10%] opacity-[0.02] rotate-12 pointer-events-none hidden lg:block">
        <img src={Logo} alt="" className="w-[600px] h-auto" />
      </div>
    </footer>
  );
}

function App() {
  const [user, setUser] = useState(undefined); // undefined → checking auth


  useEffect(() => {
    checkAuth();
  }, []);
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Not authenticated
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const ProtectedRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/admin-login" replace />;
    }
    return children;
  };

  return (
    <HashRouter>
      <ScrollToTop />
      <Toaster />
      <Navigation user={user} onLogout={handleLogout} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services/security-surveillance-v2" element={<SecuritySurveillanceV2 />} />
        <Route path="/services/computer-laptop-service" element={<ComputerLaptopService />} />
        <Route path="/services/networking-solution" element={<NetworkingSolution />} />
        <Route path="/services/system-customization" element={<SystemCustomization />} />
        <Route path="/services/sales-retail" element={<SalesRetail />} />
        <Route path="/services/mobile-solution" element={<MobileSolution />} />

        <Route path="/book-service" element={<BookService />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/support" element={<Support />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin-login" element={<AdminLogin setUser={setUser} />} />

        {/* Protected Admin Route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Footer />

      {/* ── Global Floating Action Buttons ── */}
      {/* WhatsApp */}
      <motion.a
        href="https://wa.me/919025338807"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="fixed right-5 bottom-36 z-[999] flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold rounded-full shadow-2xl shadow-green-400/30 px-4 py-3 transition-colors group"
        style={{ boxShadow: '0 8px 32px rgba(37,211,102,0.35)' }}
      >
        {/* WhatsApp SVG Icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 shrink-0">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="text-sm max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-[120px] transition-all duration-300 ease-out">Chat with Us</span>
      </motion.a>

      {/* Book Appointment */}
      <motion.a
        href="#/book-service"
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="fixed right-5 bottom-[4.5rem] z-[999] flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F4D03F] text-black font-bold rounded-full shadow-2xl shadow-yellow-400/30 px-4 py-3 transition-colors group"
        style={{ boxShadow: '0 8px 32px rgba(212,175,55,0.35)' }}
      >
        <Clock size={22} className="shrink-0" />
        <span className="text-sm max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-[160px] transition-all duration-300 ease-out">Book Appointment</span>
      </motion.a>

    </HashRouter>
  );
}

export default App;




/*Developed by: B Mohamed Naveeth */
